/**
 * PriceChart (#2457, redesigned by HOF-6) — the app-local recharts price chart
 * for a single catalog item.
 *
 * HOF-6 moved it onto the chart behaviour established by `/apps/ernteprognose`
 * (#2373/#2544): **a fixed one-year axis with optional prior-year overlays, and
 * a chart-level granularity switcher** — instead of a period preset the reader
 * then had to zoom and pan inside. Five things followed:
 *
 *  1. **The axis is fixed** (`aggregateChartModel.fixedYearDomain`): one year
 *     wide, "Heute" pinned at 20 % from the right edge, no zoom toolbar, no pan.
 *     Older seasons are reached by OVERLAYING them (`?py=`), not by scrolling —
 *     which also puts the two years on the same day-of-year, the comparison the
 *     panning was being used for.
 *  2. **The line is the volume-weighted average** of every deal in its bucket,
 *     not the unweighted median of the day's observations. See `aggregate.ts`.
 *  3. **`1T | 1W | 1M`** chooses how much time one point stands for.
 *  4. **No candles.** They needed an open and a close, and a `price_observation`
 *     has a date but no clock; the users did not read them.
 *  5. **No Seite switcher.** Kauf and Verkauf are drawn together and told apart
 *     in the legend, which is also the visibility control — one strip, so a
 *     label, colour or dash cannot drift between plot and legend (#2506's rule).
 *
 * And the diagram itself is quieter: no p10–p90 whiskers, no per-point dots. A
 * dot is drawn for the SELECTED bucket only, where it means "this is the one the
 * panel below is about".
 *
 * All shaping is the pure `aggregate` + `aggregateChartModel` (unit-tested);
 * recharts does not render under jsdom so this view stays declarative, and the
 * library behaviours CI cannot reach (`domain` honouring, `ReferenceLine`/
 * `ReferenceDot` placement on a numeric axis) are verified in a browser.
 */
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  PRICE_SIDES,
  QUALITIES,
  QUALITY_LABELS,
  SIDE_LABELS,
  type PriceSide,
  type Quality,
  type TrackedItem,
} from "./catalog";
import type { PriceSeries } from "./types";
import {
  formatQuantity,
  formatTradedQuantity,
  quantityUnit,
  resolvePublishedUnit,
} from "./publishedUnit";
import {
  VOLUME_AXIS_ID,
  VOLUME_CHIP_TITLE,
  VOLUME_COLUMN_COLOR,
  VOLUME_EXCLUDED_NOTE,
  VOLUME_INCOMPLETE_READ_NOTE,
  VOLUME_LABEL,
  VOLUME_MISSING_QUANTITY_NOTE,
  VOLUME_NO_QUANTITY_TITLE,
  VOLUME_STACK_ID,
  buildVolumePane,
  formatShare,
  volumeAxisMax,
  volumeAxisTop,
  volumeBarSizePx,
  volumeHint,
  volumeRowsWith,
  volumeUnitRefusal,
} from "./volumePane";
import {
  attachBenchmarkLayer,
  BENCHMARK_LINE_PREFIX,
} from "./benchmarkChartLayer";
import {
  benchmarkIsCoPlottable,
  benchmarkLevel,
  benchmarkPopulationLabel,
  BENCHMARK_LEVEL_ABSENT,
  BENCHMARK_LEVEL_LABEL,
  BENCHMARK_LEVEL_STATISTIC,
  BENCHMARK_LEVELS,
  BENCHMARK_UNIT,
  compactRetailerLabel,
  type BenchmarkLevel,
  type BenchmarkStatistic,
} from "./benchmarkCells";
import { dashSwatchGradient } from "./legendSwatch";
import "./benchmarkNote.css";
import { availableQualities, selectVisibleSeries } from "./axes";
import {
  ESTIMATED_LABEL,
  ESTIMATED_LEVEL_CAPTION,
  ESTIMATE_LEVEL_COLOR,
  ESTIMATE_LEVEL_DASH,
  EstimatedChip,
} from "./estimateMark";
import {
  OUT_OF_BAND_ALL_EXCLUDED_TITLE,
  OUT_OF_BAND_UNAVAILABLE_CAPTION,
  OutOfBandMarker,
} from "./outlierMark";
import { useBenchmarkLines } from "../../../hooks/useBenchmarkLines";
import { useBucketLines } from "../../../hooks/useBucketLines";
import { useElementWidth } from "../../../hooks/useElementWidth";
import {
  GRAINS,
  GRAIN_LABELS,
  GRAIN_TITLES,
  bucketEndEpochDay,
  bucketLabel,
  bucketStartEpochDay,
  epochDayToIso,
  formatDayDe,
  type Grain,
} from "./grain";
import {
  aggregateObservations,
  dropFutureObservations,
  type AggregateModel,
  type AggSourceGroup,
  type BucketAgg,
} from "./aggregate";
import { PRIOR_YEAR_OFFSETS, shiftSeriesYears } from "./priorYears";
import {
  PRIOR_YEAR_COLORS,
  TODAY_LABEL_ROW_PX,
  assignLabelSlots,
  buildAggregateChartModel,
  isoOfTs,
  priorYearLabel,
  tsOfIso,
  type AggChartModel,
  type AggLineSpec,
  type BenchmarkBucketFacts,
  type ChartView,
  type PanelColumn,
} from "./aggregateChartModel";
import {
  formatTimeTick,
  niceLinearScale,
  timeAxisTicks,
  yearBoundaryLinesAt,
  type TickLabelMetrics,
} from "../../../components/charts";
import { toEpochDay } from "./priceAt";

/**
 * y-axis tick label.
 *
 * `decimals` follows the axis STEP (#2590): below a 0,01 step two adjacent ticks
 * print identically (10,385 and 10,39 both become "10,39"). No MINIMUM is set,
 * so a coarse axis still reads `0 / 5 / 10 / 15 / 20` exactly as before.
 */
const numberFmt = (v: number, decimals = 2): string =>
  new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: Math.max(2, decimals),
  }).format(v);
const priceFmt = (v: number): string =>
  new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);

/**
 * The detail panel's traded quantity (#2649) — relocated to `publishedUnit.ts`
 * by HOF-310 as {@link formatQuantity}, unchanged, because the volume pane
 * prints the same quantity and two spellings of the kg→t rule is how one of
 * them ends up reporting litres as kilograms all over again (#2944).
 *
 * HOF-340 — the day panel reaches it through {@link formatTradedQuantity}, which
 * pairs the kg→t rule with the numerator SCALE. Taking the label without the
 * scale is the defect that issue closed, so the column may not call
 * `formatQuantity` directly; the volume pane still may, because
 * `volumeUnitRefusal` has already refused every unit whose factor is not 1.
 */

const VIEWS: readonly ChartView[] = ["alle", "kanal"];
const VIEW_LABELS: Record<ChartView, string> = {
  alle: "Alle zusammen",
  kanal: "Nach Kanal",
};
const VIEW_TITLES: Record<ChartView, string> = {
  alle: "Eine Linie je Seite, über alle Kanäle",
  kanal: "Eine Linie je Vertriebskanal",
};

/**
 * #2540 — radius of the "Heute" marker. Deliberately larger than the selected-
 * bucket dot, so it reads as an annotation rather than as one more observation
 * (there IS no observation at "Heute" — the level is projected).
 */
export const TODAY_DOT_R = 3.5;
/** Gap between the marker and its label, in px. */
export const TODAY_LABEL_OFFSET_PX = 8;
/**
 * #2540 — where the "Heute" caption sits. Exported so `todayLabelOffset.test.tsx`
 * can feed THIS value to a real recharts chart and prove the caption lands clear
 * of the value labels; a test carrying its own copy of the corner would keep
 * passing after someone changed the one below.
 *
 * ABOVE the plot frame, not `insideTopRight`, since HOF-6 §1: the marker used to
 * stand at the right-hand edge, where the top corner beside it was reliably
 * empty. Pinned at 20 % in from that edge it no longer is — whenever the highest
 * line's level lands near the top of the scale, its stroke and its price label
 * run straight through a caption placed inside the plot. The top MARGIN is the
 * one strip of the chart no line can ever reach, which is why
 * {@link CHART_MARGIN} carries the room for it.
 */
export const TODAY_CAPTION_POSITION = "top" as const;
/**
 * #2540 — right margin, so the projected value stays legible instead of being
 * clipped by the plot frame. Derived, not guessed: recharts starts the label at
 * `dotCentre + r + offset`, so the margin has to carry that lead-in PLUS the
 * string. 64 px covers `12,00 €/kg` at 11 px with headroom.
 *
 * HOF-6 made this a fixed, knowable position rather than a worst case: "Heute"
 * now always sits 20 % in from the right edge, so the label has a fifth of the
 * plot to run into and can no longer be panned against the frame.
 */
const PLOT_MARGIN_RIGHT = TODAY_DOT_R + TODAY_LABEL_OFFSET_PX + 64;

/**
 * Plot geometry, exported so `todayLabelOffset.test.tsx` can build a real chart
 * of the SAME shape and measure what `PLOT_Y_PX` asserts about it. The model
 * hardcodes that pixel height to calibrate the label-collision threshold and
 * cannot see this file; sharing the constants is what keeps the two from
 * drifting apart silently.
 */
export const CHART_HEIGHT_PX = 340;
/** `top` carries the "Heute" caption — see {@link TODAY_CAPTION_POSITION}. */
export const CHART_MARGIN = {
  top: 20,
  right: PLOT_MARGIN_RIGHT,
  bottom: 8,
  left: 8,
} as const;

/**
 * The strip recharts reserves for the value scale — the `<YAxis width>` prop
 * below, named because {@link innerPlotWidthPx} has to subtract it.
 */
export const Y_AXIS_WIDTH_PX = 64;

/**
 * HOF-96 — the width of the DRAWABLE area inside a container of `containerPx`.
 *
 * The axis-density gate is about how much room twelve labels have, which is the
 * plot rect, not the card: the margins and the y-axis strip take over 140 px
 * between them. Calibrated against a real recharts chart in
 * `todayLabelOffset.test.tsx`, the same discipline {@link CHART_HEIGHT_PX} and
 * `PLOT_Y_PX` are held to — a hardcoded geometry the model cannot see is only
 * as good as the test that measures it.
 */
export function innerPlotWidthPx(containerPx: number): number {
  return Math.max(
    0,
    containerPx - CHART_MARGIN.left - CHART_MARGIN.right - Y_AXIS_WIDTH_PX,
  );
}

/**
 * HOF-96 AC 12 — the x-axis tick label metrics, as the axis below renders them.
 *
 * `fontPx` is the `<XAxis tick={{ fontSize }}>` prop and the two are ONE fact.
 * `charEm` is a deliberate over-estimate: nothing can measure text under jsdom,
 * and the alternative to a stated over-estimate is an unstated assumption.
 * `minGapPx` is the blank two neighbouring labels have to keep — a design
 * choice, not a measurement.
 */
export const AXIS_TICK_METRICS: TickLabelMetrics = {
  fontPx: 12,
  charEm: 0.62,
  minGapPx: 6,
};

/**
 * HOF-96 AC 10–11 — what the dense ladder asks the generator for.
 *
 * BOTH numbers are load-bearing. `max` admits the monthly candidate that
 * `MAX_TICKS = 9` rejects; `target` is what makes the search PREFER it, since it
 * picks the candidate closest to the target and would otherwise still take the
 * 6-label `month, step: 2`. 13 rather than 12 because a 365-day window holds a
 * thirteenth month boundary whenever it opens exactly on a first — roughly one
 * opening date in thirty, on which "every month" has thirteen members.
 */
export const MONTHLY_TICK_OPTS = { target: 12, max: 13 } as const;

/**
 * HOF-96 AC 11–12 — the inner plot width at or above which the monthly ladder is
 * drawn. Below it the axis keeps the ladder it has always had.
 *
 * A measured gate rather than the ticket's "12 will very likely fit": that holds
 * for a desktop plot and plainly does not hold at a 320 px viewport, where the
 * page is single-column. Derived, not chosen — `PriceChartMonthlyAxis.test.tsx`
 * checks it against `minPlotPxForTicks` over this chart's real domain, its real
 * formatter and {@link AXIS_TICK_METRICS}, so a font change reds a test rather
 * than shipping an axis whose labels touch. The requirement it clears is ~465 px,
 * set by the `Dez`↔`Jan 26` pair: January carries the year at twice the width of
 * its neighbours and February is the shortest month, so the tightest pair on
 * this ladder is nowhere near its average spacing.
 */
export const MONTHLY_TICKS_MIN_PLOT_PX = 480;

/**
 * The carry-forward glyph (#2652). The chart draws three kinds of line and each
 * has to make a DIFFERENT claim at a glance, so the treatments are defined
 * against each other rather than picked one at a time:
 *
 * | line     | claim                        | stroke                      |
 * |----------|------------------------------|-----------------------------|
 * | observed | "this price was realised"    | solid 2.5px                 |
 * | Prognose | "this is where it is heading"| dashed `5 4`, 50 %, 2px     |
 * | bridge   | "nothing was traded here"    | dotted `1 5`, 40 %, 1.5px   |
 *
 * Dots rather than dashes is what separates it from the Prognose — the one
 * neighbour it could genuinely be confused with, since a trailing carry-forward
 * and a flat forecast are the same geometry (which is exactly why #2652 does not
 * draw one). It is also the lightest and thinnest of the three, because it is
 * the only one carrying no measurement at all.
 */
const BRIDGE_DASHARRAY = "1 5";
const BRIDGE_OPACITY = 0.4;
const BRIDGE_WIDTH_PX = 1.5;

/**
 * The selected bucket's marker (HOF-6 §2 — the only dot on a line now).
 *
 * Drawn as a RING (white fill, coloured stroke) rather than as a bigger disc,
 * because the plot carries a second annotation dot — "Heute"'s projected level —
 * and the two make different claims: one says "this bucket is what the panel
 * below is about", the other "this is where the line is being carried to". Size
 * alone could not tell them apart at a glance, and it is the wrong axis anyway:
 * they are usually at different x, so the reader needs to recognise a shape, not
 * compare two radii. Solid = a level; hollow = a selection.
 */
export const SELECTED_DOT_R = 4.5;
const SELECTED_DOT_STROKE_PX = 2.5;

/**
 * HOF-96 §4 — the gap between the selected marker and its value label, px.
 *
 * The same 8 px as {@link TODAY_LABEL_OFFSET_PX}, because the two labels are the
 * same kind of object seen twice: a price attached to a mark. Sharing the number
 * would couple two things that are only coincidentally equal, so it is stated
 * separately — but stated as a match, not as a fresh guess.
 */
export const SELECTED_LABEL_OFFSET_PX = 8;

/**
 * How far into the domain from the LEFT a selection may sit before its label
 * stops flipping to the right of the dot — the fallback used only while the plot
 * is UNMEASURED. See {@link selectedLabelSide} for the measured rule.
 *
 * The label goes LEFT by default, which is the ticket's own recommendation read
 * to its conclusion: *"the right edge is also where 'Heute' and the forecast tail
 * live, so three different meanings share one strip"*, and the fix it asks for is
 * to give that strip one job. A left-growing label also runs AWAY from "Heute"
 * rather than toward it, which matters because the default selection — the last
 * observed bucket — is the nearest bucket to it there is.
 *
 * The exception is the left edge, where a left-growing label runs into the
 * y-axis and its tick numbers. A quarter of the domain is roughly three months
 * of a one-year window, which is ample room on a desktop plot and far enough
 * from "Heute" (pinned at 80 %) that the two never share a side.
 */
export const SELECTED_LABEL_FLIP_FRACTION = 0.25;

/**
 * The selected label's own text metrics, as the `ReferenceDot` below renders it.
 *
 * `fontPx` is the label's `fontSize` prop and the two are ONE fact — the same
 * discipline {@link AXIS_TICK_METRICS} is held to, for the same reason: an
 * estimate stated at a size the chart does not actually draw at is not an
 * estimate. `charEm` is a deliberate over-estimate, and a wider one than the
 * axis ticks' 0.62 because these labels are drawn at `fontWeight: 600`; nothing
 * can measure text under jsdom, so the choice is between a stated over-estimate
 * and an unstated assumption.
 */
export const SELECTED_LABEL_METRICS = { fontPx: 11, charEm: 0.66 } as const;

/** What {@link SELECTED_LABEL_METRICS} says a rendered label will occupy, px. */
export function selectedLabelWidthPx(text: string): number {
  return (
    text.length * SELECTED_LABEL_METRICS.fontPx * SELECTED_LABEL_METRICS.charEm
  );
}

/**
 * Which side of its dot a selected-bucket value label sits on.
 *
 * Pure, and the only place this layer knows about the plot's edges — so the rule
 * is testable without a DOM and cannot drift into the JSX as a condition nobody
 * can name. A degenerate domain resolves to `right`, the side that is safe
 * everywhere except the extreme right of the plot, which a degenerate domain has
 * no points at.
 *
 * **The threshold is derived, not chosen** (codex round on PR #3692, [P1]). The
 * collision this avoids is measured in pixels, and a fraction of the DOMAIN is a
 * claim about time: at the narrowest supported layout the drawable plot is
 * `innerPlotWidthPx(320)` ≈ 173 px, so a quarter of it is ~43 px — while
 * `12,00 €/kg` at {@link SELECTED_LABEL_METRICS} needs ~80 px with its offset.
 * A fixed 25 % therefore printed over the y-axis for most of the second quarter
 * of a mobile plot. Given the measured width and the label's OWN text, the rule
 * is simply "left if the left fits, else right if the right fits" — which keeps
 * the left-by-default policy the docstring above argues for, and holds at 320 px
 * and at 1440 px without a second constant. Narrower than two labels and neither
 * side fits; the roomier one is then the only answer there is.
 *
 * `plot` is optional because the width is 0 until layout (and always 0 under
 * jsdom), and an unknown width must not read as "no room anywhere".
 */
export function selectedLabelSide(
  ts: number,
  domain: { lo: number; hi: number },
  plot?: { widthPx: number; text: string },
): "left" | "right" {
  const span = domain.hi - domain.lo;
  if (!(span > 0)) return "right";
  const fraction = (ts - domain.lo) / span;
  if (!plot || !(plot.widthPx > 0))
    return fraction > SELECTED_LABEL_FLIP_FRACTION ? "left" : "right";
  const needPx = selectedLabelWidthPx(plot.text) + SELECTED_LABEL_OFFSET_PX;
  const dotPx = fraction * plot.widthPx;
  if (dotPx >= needPx) return "left";
  if (plot.widthPx - dotPx >= needPx) return "right";
  return dotPx >= plot.widthPx - dotPx ? "left" : "right";
}

/**
 * HOF-6 §2 took the per-point dot off every line — which silently deletes any
 * observation that has no neighbour to be joined to. A segment of ONE bucket
 * makes recharts emit a zero-length path (`M x,y Z`) that paints nothing, so a
 * lone spring delivery, or a buy side with four receipts all year, vanishes.
 *
 * HOF-120 put a per-bucket dot back at 1W and 1M ({@link OBS_DOT_R}), which
 * makes this mark REDUNDANT at those two grains: a one-point segment now paints
 * its own dot. It is kept at every grain all the same, and deliberately. What
 * this guards against is recharts declining to draw a one-point path, and the
 * suites cannot see that either way — recharts is mocked under jsdom, so nothing
 * here observes real SVG. Dropping the mark on the strength of an unverifiable
 * assumption trades a 0,5 px inconsistency (a lone bucket reads at r=3 rather
 * than r=2,5, concentric and in the same colour, so it reads as one dot) for the
 * silent loss of an observation. That is the wrong side of that trade.
 *
 * Worse than vanishing, in fact: the bridge into that bucket and the forecast out
 * of it still hold its LEVEL, so the reader is shown a carried price with no
 * evidence anywhere of the measurement it was carried from.
 *
 * So exactly those buckets keep a mark, and no others — "no dots" is a statement
 * about density, not a reason to drop data. Drawn as a filled disc in the line's
 * own colour with no ring: it is not an annotation about the line the way the
 * "Heute" and selected markers are, it IS the line, collapsed to a point.
 */
export const LONE_OBS_DOT_R = 3;

/**
 * HOF-120 — radius of the dot on an observed bucket at `1W` and `1M`.
 *
 * HOF-6 §2 argued `dot={false}` everywhere from DENSITY, and at `1T` that still
 * decides it: 365 marks over a fixed year is a dotted rule, not a chart. At the
 * two coarse grains the density is 52 and 12, and there the dots carry something
 * the stroke cannot say — *where the data is*. {@link GAP_BREAK_DAYS} only
 * breaks the line at gaps wider than six weeks, so anything shorter is
 * interpolated silently and up to six weeks of no trade reads as a normal
 * stretch of trading, at a plausible-looking level. That is not a density
 * problem; it is the line asserting something nobody observed.
 *
 * 2,5 px is deliberately the radius the per-observation dot had BEFORE §2
 * removed it, and it is smaller than every mark on this plot that annotates
 * rather than reports ({@link LONE_OBS_DOT_R} 3, {@link TODAY_DOT_R} 3,5,
 * {@link SELECTED_DOT_R} 4,5). §2's other argument was that the selected dot
 * earns its meaning by being one of the few marks present, so staying strictly
 * below those is the real constraint here — `PriceChartObservedDots` pins it as
 * an inequality against the constants themselves. (The out-of-band ring used to
 * be the binding one at r=6; it was removed from the plot — Antonio, 2026-09-02
 * — and the plausibility verdict now lives only where it can be read, in the
 * bucket panel's row.)
 *
 * It is NOT drawn on a bridge or on the Prognose, and neither needs a condition
 * here: both are separate `<Line>`s that keep `dot={false}`. A bridged stretch
 * contributes no clickable bucket (#2652), so growing dots there would promise a
 * drill-down the panel cannot give — the dots mark exactly what can be broken
 * down.
 */
export const OBS_DOT_R = 2.5;

/**
 * What one point stands for, in the footer's own sentence. Declined German
 * ("ein Tag" / "eine Woche"), so the clause reads as a sentence rather than as a
 * label with a noun appended.
 */
const GRAIN_POINT_COPY: Record<Grain, string> = {
  day: "ein Tag",
  week: "eine Woche",
  month: "ein Monat",
};

export interface PriceChartProps {
  item: TrackedItem;
  series: PriceSeries[];
  /** `?g=` — how much time one point stands for (HOF-6 §3). */
  grain: Grain;
  onGrainChange: (g: Grain) => void;
  quality?: Quality;
  onQualityChange: (q: Quality) => void;
  view: ChartView;
  onViewChange: (v: ChartView) => void;
  /** Enabled prior-year overlays (`?py=`), ascending — 1, 2 and/or 3 (HOF-6 §1). */
  priorYears: readonly number[];
  onTogglePriorYear: (offset: number) => void;
  today: string;
  /**
   * #2508 — a deep-linked observation day (`?d=` on the page). Resolved to the
   * BUCKET containing it, so a link minted at 1T grain still points at the right
   * point after the reader switches to 1M. The reader's own click still wins
   * from then on.
   */
  day?: string | null;
  /**
   * #2652 — whether `series` is the WHOLE of the tenant's price history, which
   * is what licenses bridging an empty stretch with a carry-forward line.
   *
   * The page derives it from the read's own `data_quality`: complete means
   * neither `truncated` (the server's cell cap fired) nor `degraded` (part of
   * the population could not be read). Both are facts about one read, and both
   * are answered the same way here, because a bridge is a claim that NOTHING
   * happened in a window and neither flag leaves that claim standing.
   *
   * Defaults to `false` — the state that makes no claim.
   */
  dataComplete?: boolean;
  /**
   * #2944 — the units this item's live facts were PUBLISHED under, from the
   * index read (`usePriceIndex().unitsByItem`).
   *
   * Every unit the chart prints used to come from `item.unit`, i.e. from the
   * catalog at render time — so editing the catalog silently relabelled every
   * price already plotted. They now come from the data, and this prop is how the
   * data's unit reaches a chart whose cells could not carry one.
   *
   * Omitted means the chart states no unit rather than guessing the catalog's.
   */
  publishedUnits?: readonly string[];
  /**
   * #2944 — the units the CURATED price book's facts were published under.
   *
   * Separate from {@link publishedUnits} because the two populations can be
   * quoted differently and the chart renders both: the plot is booked, the
   * reference level is the book. `publishedUnits` deliberately hides the book's
   * unit behind the trades' (booked always wins), which makes it exactly the
   * wrong thing to label the book with.
   */
  publishedEstimateUnits?: readonly string[];
  /**
   * HOF-19 — the server could not compute the plausibility marks for this read,
   * so nothing on this chart is marked because it was UNCHECKED rather than
   * because it passed (`useItemPriceSeries().dataQuality.marksUnavailable`).
   *
   * The chart must say so, and this prop is why the flag exists at all. No cell
   * is missing when it fires — the line, the panel and the bridges are all
   * exactly as complete as usual — so an unread flag would leave a failed check
   * and a clean chart rendering identically. That is the same shape as #2573's
   * "a farm with 20.000 observations shown the same screen as a farm with none",
   * one level in.
   *
   * Defaults to `false`, the state that makes no claim.
   */
  marksUnavailable?: boolean;
  /**
   * HOF-19 Phase B — this item's benchmark series
   * (`useItemPriceSeries().benchmarkSeriesByItem[itemId]`), one per drawn
   * MARKET LEVEL: what the end customer pays, and what the Großmarkt pays.
   *
   * A labelled REFERENCE, never a yardstick: the consumer level is an
   * Endverbraucherpreis carrying a 15-60 % unknown Händler markup, so the level
   * is drawn as-is with that caveat in words on the screen, and nothing is ever
   * derived from it. The chart attaches it AFTER the own-price model is built
   * (see `attachBenchmarkLayer`), so no own statistic can move.
   */
  benchmarkSeries?: PriceSeries[];
  /**
   * The CONSUMER line's read failed for this answer
   * (`dataQuality.consumerBenchmarkDegraded`). Said in words, because "there is
   * no consumer data here" and "we could not load it" are different sentences
   * and only one of them is a statement about the market.
   */
  consumerBenchmarkDegraded?: boolean;
  /**
   * That read hit its cap — cells are MISSING, so a drawn line is INCOMPLETE
   * (codex P1: without this the partial read draws as a complete market
   * reference). Distinct from the degraded flag the way the own flags are:
   * "we stopped early" vs "part of it was unreadable".
   */
  consumerBenchmarkTruncated?: boolean;
  /**
   * The same two facts for the TRADE line
   * (`dataQuality.tradeBenchmarkDegraded` / `…Truncated`).
   *
   * Their own props rather than an OR: the failure sentences name their line,
   * the two reads fail independently, and a message naming the wrong line is
   * worse than none.
   */
  tradeBenchmarkDegraded?: boolean;
  tradeBenchmarkTruncated?: boolean;
  /**
   * Which levels the server LOOKED for (`dataQuality.benchmarkLevels`).
   *
   * Coverage is asymmetric by source — the retail collectors cover the four
   * berries, the Großmarkt report covers Erdbeeren and Kirschen — so a missing
   * line is normal and, unsaid, reads as a broken chart. This is what lets the
   * chart say "no Großmarkt index for this product" instead of showing nothing
   * and leaving the reader to guess whether that is data or a defect.
   */
  benchmarkLevels?: readonly string[];
}

interface SegmentProps<T extends string> {
  label: string;
  values: readonly T[];
  labelOf: (v: T) => string;
  titleOf?: (v: T) => string;
  selected: T | undefined;
  available: readonly T[];
  onSelect: (v: T) => void;
}

function Segment<T extends string>({
  label,
  values,
  labelOf,
  titleOf,
  selected,
  available,
  onSelect,
}: SegmentProps<T>) {
  return (
    <div className="preisspiegel-seg" role="group" aria-label={label}>
      <div className="preisspiegel-seg__buttons">
        {values.map((v) => (
          <button
            key={v}
            type="button"
            className="preisspiegel-seg__btn"
            aria-pressed={selected === v}
            disabled={!available.includes(v)}
            {...(titleOf ? { title: titleOf(v) } : {})}
            onClick={() => onSelect(v)}
          >
            {labelOf(v)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * HOF-214 — what the two headline chips' numbers actually ARE. Two different
 * statistics share one chart (a mengengewichteter Durchschnitt over the farm's
 * own deals, a chained matched-model index over the AMI panel) and nothing else
 * on screen says so; the ⓘ dot on the chip carries the sentence. German copy
 * per the issue; the Verkauf text names the current year.
 */
function verkaufChipInfo(today: string): string {
  return (
    `Eigene Verkaufspreise ${today.slice(0, 4)}: mengengewichteter Durchschnitt ` +
    "aller eigenen Verkäufe im jeweiligen Zeitraum, über alle Kanäle."
  );
}

/**
 * One sentence per drawn LEVEL. Same index METHOD on both lines and
 * deliberately said in the same words — what differs is the point in the chain
 * the price sits at, and that is the whole of what the reader has to know to
 * compare it against the right one of his own channels.
 *
 * Neither names a supplier. Which panels and collectors fed a line is a
 * per-row fact, visible in the drill-down; on the chip it would be a claim
 * about sources this tenant may not have.
 */
/**
 * The one-line caveat under the plot, per level — what the line IS, not how it
 * is computed (that is the ⓘ chip's job).
 *
 * Each names the farm's own channel it is the yardstick FOR. Without that, the
 * two lines are just two dashes 4 €/kg apart, and the reader's own guess about
 * which one his price belongs beside is precisely the thing that goes wrong:
 * measured against the shelf, every Großhandel price looks robbed. Hue and
 * dash tell the two APART; only this sentence says which is which.
 */
const BENCHMARK_LEVEL_CAVEAT: Record<BenchmarkLevel, string> = {
  consumer:
    "Benchmark Verbraucherebene: Endverbraucherpreise mit unbekanntem " +
    "Händler-Aufschlag — der Vergleich für die Direktvermarktung.",
  trade:
    "Benchmark Handelsebene: Notierungen der deutschen Großmärkte für " +
    "deutsche Ware und HofSync-Abschlüsse — der Vergleich für Großhandel und " +
    "Einzelhandel, nicht für die Direktvermarktung.",
};

const BENCHMARK_CHIP_INFO: Record<BenchmarkLevel, string> = {
  consumer:
    "Benchmark Verbraucherebene (konventionell & Bio): Endverbraucherpreise " +
    "aus dem Einzelhandel — veröffentlichte Regal- und Angebotspreise sowie " +
    "erhobene Ladenpreise — mit unbekanntem Händler-Aufschlag. Der " +
    "Vergleichsmaßstab für die Direktvermarktung. Dargestellt als " +
    "verketteter Preisindex: Es fließen " +
    "nur Preisänderungen identischer Produkte ein (gleiche Kette, Stadt, " +
    "Packungsgröße, Bio/konventionell), normiert auf €/kg. Fehlende Meldungen " +
    "einzelner Geschäfte verzerren den Verlauf dadurch nicht.",
  // HOF-544 — two populations and two methods, named without a single number:
  // the weights are a calibration placeholder, and a figure here would outlive
  // the recalibration that changes it.
  trade:
    "Benchmark Handelsebene (deutsche Ware): Großmarkt-Notierungen deutscher " +
    "Ware aus dem BLE-Marktbericht und HofSync-Abschlüsse — anonymisierte, " +
    "abgerechnete Verkaufspreise teilnehmender HofSync-Betriebe. Der " +
    "Vergleichsmaßstab für Großhandel und Einzelhandel, also für das, was der " +
    "Betrieb selbst erlöst. Nicht mit der Verbraucherebene vergleichbar: " +
    "dazwischen liegt die Handelsspanne. Die Notierungen gehen als " +
    "mengengewichteter Wochendurchschnitt der meldenden Großmärkte ein, die " +
    "HofSync-Abschlüsse als Index: Preisänderungen je Abnehmer verkettet, " +
    "fehlende Wochen verzerren den Verlauf nicht. Liegen beide vor, fließen " +
    "sie zu festen Anteilen ein; sonst trägt die vorhandene Quelle die Woche " +
    "allein. In €/kg. Einzelne Abschlüsse, Abnehmer oder Betriebe werden nie " +
    "gezeigt. Importware fließt nicht ein: sie ist ein anderer Preis für ein " +
    "anderes Erzeugnis.",
};

/**
 * What a benchmark line's visibility is decided BY (HOF-269 §1).
 *
 * Its LEVEL wherever this build knows one, so the reader's choice survives an
 * item switch — a line id embeds the catalog item, and a per-id decision would
 * be undone by picking the next berry.
 *
 * Its own series id where the level is unknown — a level a later server draws
 * and this build has no name for. Falling back to a shared key would give every
 * such line one switch; falling back to "always hidden" would give it a chip
 * that does nothing, which is the worse failure of the two and the one this
 * file's own rules keep naming. Keyed by id it is merely re-defaulted on an
 * item switch, which is the pre-HOF-269 behaviour for a line nobody can name.
 */
function benchmarkVisibilityKey(lineId: string): string {
  const seriesId = lineId.slice(BENCHMARK_LINE_PREFIX.length);
  return benchmarkLevel(seriesId) ?? seriesId;
}

/**
 * Does this level's `n` count OBSERVATIONS — the rows the drill-down lists?
 *
 * A property of the POPULATION, never of the bucket, and the two look alike in
 * exactly one place. `weekly_cells_from_aggregate_items` emits `n` as the count
 * of notations behind the week; `_chain_one_year` emits it as `support[wk]`,
 * the count of chain RELATIVES — which is 0 at every segment anchor by
 * construction, and which the re-bucket then reads as one. That anchor week is
 * also the one the server marks `weighted_mean`, because its value IS the mean
 * of its rows and earns the `Ø`. So gating the COUNT on the bucket's statistic
 * printed "1 Beobachtung" over a week holding any number of shelf visits
 * (codex round 2, P1) — the `Ø` gate is per bucket and this one is per level,
 * and the re-anchored consumer week is precisely where they part.
 *
 * An unknown level counts nothing: a number whose meaning this build cannot
 * name is not one it may put a noun on.
 */
function benchmarkCountIsObservations(level: BenchmarkLevel | null): boolean {
  return level != null && BENCHMARK_LEVEL_STATISTIC[level] === "mean";
}

/**
 * One legend chip. A `button` with `aria-pressed`, never a passive swatch: the
 * legend IS the visibility control since HOF-6 folded the channel chips into it,
 * so a chip that only looked like a legend entry would hide an affordance.
 *
 * The swatch is drawn from the line's own `color`/`dash`/`strokeWidth`, so a
 * dashed Kauf line's chip is dashed too — the plot and the legend cannot
 * disagree about which stroke belongs to which name (#2506's rule). Since
 * HOF-214 it carries the dash PATTERN and not just the fact of one: with three
 * dashed strokes on the plot, "dashed" had stopped being an identity — see
 * `legendSwatch.ts`.
 */
function LegendChip({
  label,
  color,
  dash,
  pressed,
  value,
  onToggle,
  title,
  info,
  disabled = false,
  testAttrs,
}: {
  label: string;
  color: string;
  dash?: string;
  pressed: boolean;
  value?: string;
  onToggle: () => void;
  title?: string;
  /**
   * HOF-214 — one sentence saying what the chip's KPI is. Renders a small ⓘ
   * dot inside the chip and the text as a pure-CSS tooltip on hover and
   * keyboard focus (`data-tip`); assistive tech reads it via
   * `aria-description`. The dot itself is decorative (`aria-hidden`).
   */
  info?: string;
  /**
   * HOF-96 AC 9 — the option is offered but cannot be taken here. `disabled`
   * rather than "not rendered": a chip that vanishes in one view takes the
   * reader's model of what the chart can do with it, and a control that is
   * absent cannot explain itself. Disabled + `title` can.
   *
   * It also does the enforcing. `onToggle` writes `?py=`, and AC 8 says the view
   * switch must not rewrite that param — `disabled` is what makes that true at
   * the source rather than by nobody happening to click.
   */
  disabled?: boolean;
  /** Which KIND of chip this is — a line's, or a prior-year offset's. */
  testAttrs: Record<string, string>;
}) {
  // The swatch wears the line's OWN dash, not merely the fact of one — three
  // dashed strokes share this plot since HOF-214. `null` (an unreadable
  // dasharray) leaves the stylesheet's generic dashed gradient in place.
  const swatchDash = dash ? dashSwatchGradient(dash) : null;
  return (
    <button
      type="button"
      className="preisspiegel-chart__chip"
      aria-pressed={pressed}
      disabled={disabled}
      {...(title ? { title } : {})}
      {...(info ? { "data-tip": info, "aria-description": info } : {})}
      {...testAttrs}
      onClick={onToggle}
      style={{ ["--cc" as string]: color } as CSSProperties}
    >
      <span
        className={
          dash
            ? "preisspiegel-chart__chip-sw is-dashed"
            : "preisspiegel-chart__chip-sw"
        }
        aria-hidden="true"
        {...(swatchDash ? { style: { ["--sw-dash" as string]: swatchDash } as CSSProperties } : {})}
      />
      <span className="preisspiegel-chart__chip-label">{label}</span>
      {value != null && (
        <span className="preisspiegel-chart__chip-val">{value}</span>
      )}
      {info != null && (
        <span className="preisspiegel-chart__chip-info" aria-hidden="true">
          i
        </span>
      )}
    </button>
  );
}

export default function PriceChart({
  item,
  series,
  grain,
  onGrainChange,
  quality,
  onQualityChange,
  view,
  onViewChange,
  priorYears,
  onTogglePriorYear,
  today,
  day = null,
  dataComplete = false,
  publishedUnits,
  publishedEstimateUnits,
  marksUnavailable = false,
  benchmarkSeries = [],
  consumerBenchmarkDegraded = false,
  consumerBenchmarkTruncated = false,
  tradeBenchmarkDegraded = false,
  tradeBenchmarkTruncated = false,
  benchmarkLevels = BENCHMARK_LEVELS,
}: PriceChartProps) {
  // ── #2777 — the curated price book is separated FIRST, before anything ─────
  //
  // Everything below this line that computes a number — the weighted average,
  // the panel, the y-scale, the bridge — is made of `booked`. An estimate is a
  // reference price the materialiser derived for a crop with no trade behind it,
  // all of them stamped on one pinned anchor date (#2538); let one into
  // `aggregateObservations` and it becomes a trading day of its own, on a date
  // nothing was sold, with no volume to weight it by.
  //
  // The axis CONTROLS still read the full list: an estimate-only item has to
  // keep its Qualität segment, or drilling into one of the #2694 Saatgut tiles
  // lands on a chart with no controls and no explanation.
  const booked = useMemo(() => series.filter((s) => !s.estimate), [series]);
  const estimates = useMemo(() => series.filter((s) => s.estimate), [series]);

  // Side-agnostic since HOF-6 §5: the chart draws both sides, so it offers every
  // Qualität either of them is backed on.
  const qualities = availableQualities(series);

  // Future-dated observations go BEFORE aggregation, not at the bucket clip —
  // see `dropFutureObservations` for why the clip cannot catch them once a
  // week or month bucket has folded one in.
  const filtered = useMemo(
    () =>
      dropFutureObservations(selectVisibleSeries(booked, { quality }), today),
    [booked, quality, today],
  );
  const model: AggregateModel = useMemo(
    () => aggregateObservations(filtered, grain),
    [filtered, grain],
  );

  const hasChannels = useMemo(
    () =>
      model.sidesPresent.some(
        (s) => (model.channelsBySide[s] ?? []).length > 1,
      ),
    [model],
  );
  const effView: ChartView = hasChannels ? view : "alle";

  /**
   * HOF-96 §2 — the offsets actually drawn, which is NONE in `Nach Kanal`.
   *
   * `priorYears.ts` designed the overlays for the single-line `Alle zusammen`
   * case: *"drawn thin and grey under the live line"*, singular. `Nach Kanal`
   * already spends one line per channel, so three offsets there is twelve lines
   * on one plot, nine of them grey — the context out-drawing the series it
   * exists to contextualise.
   *
   * Read off `effView`, not off the `view` PROP: a single-channel item falls
   * back to `alle` whatever `?view=` says, and gating on the raw prop would
   * strip the overlays off the one chart shape they were built for.
   *
   * **`?py=` is not touched** (AC 8). This is a rendering decision, so switching
   * `kanal → alle` restores exactly the offsets the reader selected, with no
   * remembered state anywhere outside the URL — the reason spec A1 took this
   * over forcing `py=[]` on the view switch.
   */
  const overlayYears = useMemo(
    () => (effView === "kanal" ? [] : priorYears),
    [effView, priorYears],
  );

  // The overlays, one aggregate per enabled offset. Shifted BEFORE aggregation
  // so a prior year's buckets land on the live grid — see `priorYears.ts`.
  const priorModels = useMemo(() => {
    const out: Record<number, AggregateModel> = {};
    for (const offset of overlayYears) {
      out[offset] = aggregateObservations(
        shiftSeriesYears(filtered, offset),
        grain,
      );
    }
    return out;
  }, [filtered, overlayYears, grain]);

  /**
   * The reference prices for the CURRENT selection — one PER SIDE, newest wins
   * within a side.
   *
   * One number and one date each, not a series: the book holds a single anchored
   * value per (item, side), so the honest rendering is a level and a sentence —
   * a line joining points would draw movement nobody observed. Where the book
   * holds several for one side, the newest wins, mirroring how the tile takes
   * its headline off the primary series' last point.
   *
   * **Per side, because HOF-6 §5 removed the SEITE switcher.** This used to run
   * inside a single-side selection, so one winner was the whole answer. With
   * both sides on the plot, a single `best` silently drops one side's book price
   * and prints the other as though it governed both lines — on exactly the items
   * where it matters most, since the sixteen catalog entries tracking both sides
   * are the bought-AND-sold ones (Futterweizen, Heu, Stroh, Raps).
   */
  const estimateMarks = useMemo(() => {
    const visible = selectVisibleSeries(estimates, { quality });
    const bySide = new Map<
      PriceSide,
      { side: PriceSide; value: number; date: string }
    >();
    for (const s of visible) {
      const last = s.points[s.points.length - 1];
      if (!last) continue;
      const held = bySide.get(s.side);
      if (!held || last.date > held.date)
        bySide.set(s.side, {
          side: s.side,
          value: last.value,
          date: last.date,
        });
    }
    // Canonical side order, so two levels are always captioned in the same
    // sequence as the legend names their lines.
    return PRICE_SIDES.flatMap((side) => {
      const mark = bySide.get(side);
      return mark ? [mark] : [];
    });
  }, [estimates, quality]);
  /** Whether the side has to be NAMED — only ambiguous once there are two. */
  const namesSide = estimateMarks.length > 1;

  // ── #2944 — TWO units, because the chart draws two populations ──────────────
  //
  // Everything the chart PLOTS is booked (`filtered` → `model` → axis, panel,
  // today label), and the reference level is a separate mark with its own value.
  // Labelling both from one resolution would restate a €/l book price as €/kg on
  // a farm that trades in kilos — the #2944 bug, reintroduced inside the fix.
  //
  // `unitLabel` resolves over the population the chart SPEAKS FOR, which is
  // `booked` where there is any and the book otherwise — the same "booked always
  // wins" cut `buildTileModel` makes, and the same one behind `publishedUnits`.
  const speaksFor = booked.some((s) => s.points.length > 0)
    ? booked
    : estimates;
  const published = useMemo(
    () => resolvePublishedUnit(speaksFor, publishedUnits),
    [speaksFor, publishedUnits],
  );
  const unitLabel = published.unit ?? "";
  const estimateUnitLabel = useMemo(
    () => resolvePublishedUnit(estimates, publishedEstimateUnits).unit ?? "",
    [estimates, publishedEstimateUnits],
  );

  // Legend visibility. Keyed by LINE ID rather than by channel (#2486's
  // hiddenChannels), because a line is now (side × channel) and the two
  // dimensions have to be switchable independently.
  const [hiddenLineIds, setHiddenLineIds] = useState<Set<string>>(new Set());

  /**
   * HOF-269 §1 — which benchmark lines the reader has switched on, this mount.
   *
   * The page used to open with `Verkauf`, `Benchmark Verbraucherebene` and
   * `Benchmark Handelsebene` all drawn. The two benchmarks are references — a
   * shelf price in somebody else's shop and a Großmarkt notation — and they sit
   * about 4 €/kg from the farm's own price and from each other. A farmer who
   * opens the page to read *what did I get for Erdbeeren* was handed two other
   * people's prices in the same visual field, and his own guess about which
   * line his price belongs beside is exactly what the two-level split exists to
   * prevent (`BENCHMARK_LEVEL_CAVEAT`). So the benchmark is asked for.
   *
   * Held as a SHOWN set per LEVEL rather than as hidden ids in
   * `hiddenLineIds`, and both halves of that are load-bearing.
   *
   * *Shown*, because the default is "off": an id that has never been seen is
   * hidden by construction, so there is no seeding effect to run, no frame in
   * which a benchmark is drawn before its default lands, and no way for the
   * reconcile effect below to un-hide one.
   *
   * *Keyed by {@link benchmarkVisibilityKey}* — the LEVEL — because a benchmark
   * line id embeds the catalog item (`bm__erdbeeren__benchmark__consumer`).
   * Keyed by id, every id would be new after an item switch and the reader's
   * own choice would be undone by picking the next berry — which #2486's rule
   * (the hide-set survives an item, grain and view switch) forbids. The level
   * is the thing the reader actually decided about, and it is stable across
   * items.
   */
  const [shownBenchmarkKeys, setShownBenchmarkKeys] = useState<Set<string>>(
    new Set(),
  );

  // ── HOF-19 Phase B — whether the AMI benchmark may share this axis ─────────
  //
  // AC 9: AMI is €/kg by construction, and a great many items are not (€/t and
  // €/dt are factors of 1000 and 10 away). Two lines on one axis in different
  // units is #2944's substitution bug with both numbers looking plausible, so
  // the answer is to REFUSE and say so below — never to convert, which would
  // put a derived number on a chart whose whole argument is that it shows
  // observations. A `mixed` unit set refuses for the same reason the axis
  // label is suppressed: there is no one unit this axis can honestly claim.
  const benchmarkHasData = benchmarkSeries.some((s) => s.points.length > 0);
  const benchmarkCoPlottable =
    !published.mixed && benchmarkIsCoPlottable(published.unit ?? undefined);

  /**
   * The benchmark line ids that are hidden right now — every one whose level
   * the reader has not switched on. Derived from the SERIES rather than from
   * the model, because the model's benchmark specs are built from this.
   */
  const hiddenBenchmarkIds = useMemo(
    () =>
      benchmarkSeries
        .map((s) => `${BENCHMARK_LINE_PREFIX}${s.seriesId}`)
        .filter((id) => !shownBenchmarkKeys.has(benchmarkVisibilityKey(id))),
    [benchmarkSeries, shownBenchmarkKeys],
  );

  const chart = useMemo(() => {
    const base = buildAggregateChartModel({
      model,
      priorModels,
      view: effView,
      hiddenLineIds: [...hiddenLineIds],
      today,
      grain,
      bridgeGaps: dataComplete,
    });
    // Attached AFTER the build, so nothing the own-price model computed can
    // move (the layer's tests pin that byte-for-byte). Identity when there is
    // nothing to draw, so absence renders the pre-HOF-19 chart exactly.
    if (!benchmarkHasData || !benchmarkCoPlottable) return base;
    return attachBenchmarkLayer(base, benchmarkSeries, {
      grain,
      today,
      quality,
      // Both, because the two sets answer different questions: `hiddenLineIds`
      // is the reader's own-line hide-set, `hiddenBenchmarkIds` is §1's
      // level default. The layer only ever reads them as one lookup.
      hiddenLineIds: [...hiddenLineIds, ...hiddenBenchmarkIds],
    });
  }, [
    model,
    priorModels,
    effView,
    hiddenLineIds,
    today,
    grain,
    dataComplete,
    benchmarkHasData,
    benchmarkCoPlottable,
    benchmarkSeries,
    hiddenBenchmarkIds,
    quality,
  ]);

  /**
   * HOF-310 — the Volumen pane: the tenant's own sold volume as columns under
   * the price plot.
   *
   * **Component state, `useState`, nothing persisted** (spec A4). The issue
   * asked whether to remember a reader's "on" *"per browser like the other
   * chips"*, and the premise turned out to be false — no Preisspiegel chip
   * persists (`hiddenLineIds` and `shownBenchmarkKeys` are both `useState`; the
   * only value in `localStorage` is the Kategorie). So "like the other chips"
   * MEANS not persisted, which also protects the decided default of off: a
   * remembered "on" would quietly undo it after the reader's first visit.
   */
  const [volumeOn, setVolumeOn] = useState(false);

  const domainLo = chart.domain.lo;
  const domainHi = chart.domain.hi;
  /**
   * The pane's model — built off the LIVE aggregate and `effView` only.
   *
   * Never off `priorModels` (AC 12): a prior-year overlay is history displaced
   * onto this year's grid, so its volume would stack a 2025 delivery onto a 2026
   * column at the same x. And never off the benchmark layer: those are other
   * people's prices and carry no own volume at all.
   */
  const volumePane = useMemo(
    // Keyed on the domain's two NUMBERS, not on `chart.domain`: the object is
    // rebuilt on every legend click, and the pane is a fold over the whole year.
    () =>
      buildVolumePane({
        model,
        view: effView,
        domain: { lo: domainLo, hi: domainHi },
        today,
      }),
    [model, effView, domainLo, domainHi, today],
  );

  /**
   * Whether the pane can be drawn AT ALL — a property of the data, deliberately
   * not of the current line visibility.
   *
   * A chip disabled because the reader has just hidden a line would be a control
   * that comes and goes under them; a chip disabled because no deal states a
   * quantity, or because the tile carries two units, is a control that explains
   * itself — the same shape as the prior-year chips' `disabled` + `title` in
   * "Nach Kanal", and the house rule that an unavailable option carries its own
   * reason rather than sending the reader hunting for a caption.
   */
  const volumeUnitRefused = volumeUnitRefusal(published.unit, published.mixed);
  const volumeAvailable = volumePane != null && volumeUnitRefused == null;
  const volumeChipTitle =
    volumeUnitRefused ??
    (volumePane == null ? VOLUME_NO_QUANTITY_TITLE : VOLUME_CHIP_TITLE);

  /**
   * AC 10 / AC 11 — the segments whose own price line is ACTUALLY drawn.
   *
   * Read off each line spec's own `hidden`, never off the click that set it:
   * `survivesHiding` refuses a toggle that would empty the plot, so a refused
   * `Verkauf` click must leave the pane standing — the line is still there. In
   * "Alle zusammen" this degenerates to the issue's own rule, since the side's
   * single line is the only member.
   */
  const visibleVolumeKeys = useMemo(() => {
    if (volumePane == null) return [];
    const drawn = new Set(
      chart.lines.filter((l) => l.offset === 0 && !l.hidden).map((l) => l.id),
    );
    return volumePane.series.filter((s) => drawn.has(s.lineId)).map((s) => s.key);
  }, [volumePane, chart.lines]);

  /**
   * Is the pane on the plot right now? Every volume element gates on this one.
   *
   * At least one visible series must be MEASURED: a plot whose only visible
   * volume series has nothing to draw is a hidden axis and a tooltip about
   * columns that are not there.
   */
  const volumeDrawn =
    volumeOn &&
    volumeAvailable &&
    volumePane != null &&
    volumePane.series.some((v) => v.measured && visibleVolumeKeys.includes(v.key));

  /**
   * The rows recharts is handed.
   *
   * With the pane off this IS `chart.rows`, by reference — AC 1's "renders
   * exactly as it does today" is then structural rather than a claim, since
   * there is no volume key on the row object for anything to read.
   */
  const chartRows = useMemo(
    () =>
      volumeDrawn && volumePane != null
        ? volumeRowsWith(chart.rows, volumePane, visibleVolumeKeys)
        : chart.rows,
    [volumeDrawn, volumePane, chart.rows, visibleVolumeKeys],
  );

  /** The unit the columns are denominated in — #2944's rule, one column over. */
  const qtyUnitLabel = quantityUnit(published.unit);

  /**
   * The hover hint for one bucket, or null.
   *
   * This is the first thing this chart's tooltip has ever rendered: the #2540
   * decision replaced the heavy tooltip with the click-to-open detail panel, and
   * HOF-96 AC 17 pinned "the crosshair still says nothing". That rule survives
   * intact — with the pane off this returns null for every bucket, so the
   * crosshair says nothing exactly as it did, and it only ever speaks about the
   * columns the reader switched on.
   */
  const renderVolumeHint = (
    // DESTRUCTURED with a default, exactly as every component in this file
    // declares its props: `eslint-plugin-react` reads a JSX-returning function
    // as a component, and it can only resolve prop types off the destructuring
    // pattern — `props?: {…}` then errors as unvalidated props. The default is
    // what lets recharts' own `content()` call arrive with no argument at all.
    { payload }: { payload?: { payload?: { date?: string } }[] } = {},
  ) => {
    if (!volumeDrawn || volumePane == null) return null;
    const date = payload?.[0]?.payload?.date;
    if (!date) return null;
    const hint = volumeHint(volumePane, date, visibleVolumeKeys);
    if (hint == null) return null;
    return (
      <div className="preisspiegel-chart__vol-tip">
        {volumePane.stacked && (
          <ul className="preisspiegel-chart__vol-tip-rows">
            {hint.rows.map((r) => (
              <li key={r.key}>
                <span
                  className="preisspiegel-chart__vol-tip-sw"
                  aria-hidden="true"
                  style={{ background: r.color }}
                />
                <span className="preisspiegel-chart__vol-tip-name">
                  {r.label}
                </span>{" "}
                <span className="preisspiegel-chart__vol-tip-qty">
                  {formatQuantity(r.qty, qtyUnitLabel)}
                </span>
                {r.share != null && <span> · {formatShare(r.share)}</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="preisspiegel-chart__vol-tip-total">
          {VOLUME_LABEL}: {formatQuantity(hint.total, qtyUnitLabel)}
        </p>
        {/* Three disclosures, each firing only when its own condition really
            holds — a note about a rule that did not fire is the same defect as
            copy about a glyph the reader cannot see (#2777, HOF-19 AC 17).

            AC 5: the disagreement between this column and the line above it is
            STATED rather than discovered. Codex round 1 P1: the total is a lower
            bound wherever a deal states no quantity, and the shares are then
            shares of that lower bound. Codex round 1 P1: a Σ needs
            `dataComplete` more than a mean does — a mean over a subset is still
            a plausible mean, a total over one is simply too small. */}
        {hint.excludedFromPrice > 0 && (
          <p className="preisspiegel-chart__vol-tip-note">
            {VOLUME_EXCLUDED_NOTE}
          </p>
        )}
        {hint.missingCount > 0 && (
          <p className="preisspiegel-chart__vol-tip-note">
            {VOLUME_MISSING_QUANTITY_NOTE}
          </p>
        )}
        {!dataComplete && (
          <p className="preisspiegel-chart__vol-tip-note">
            {VOLUME_INCOMPLETE_READ_NOTE}
          </p>
        )}
      </div>
    );
  };

  // Whether an AMI line is actually ON the plot — the caveat sentence's gate,
  // read off the model rather than recomputed, so the sentence and the glyph
  // cannot disagree (the #2652 rule, applied to this layer).
  const benchmarkOnPlot = chart.drawn.some((l) =>
    l.id.startsWith(BENCHMARK_LINE_PREFIX),
  );
  /** Is anything but a reference actually drawn? The footer's copy turns on it. */
  const ownOnPlot = chart.drawn.some(
    (l) => !l.id.startsWith(BENCHMARK_LINE_PREFIX),
  );

  // HOF-248 — WHICH population(s) the caveat is about. Taken off the drawn
  // lines for the same reason the gate above is: the sentence and the glyph
  // must not be able to disagree. Every one of these sentences used to say
  // "AMI MarktMonitor" unconditionally, so a tenant with no AMI licence read a
  // claim about a panel it does not have, over a line that is not AMI's.
  const drawnBenchmarkSeriesIds = chart.drawn
    .filter((l) => l.id.startsWith(BENCHMARK_LINE_PREFIX))
    .map((l) => l.id.slice(BENCHMARK_LINE_PREFIX.length));
  const drawnBenchmarkLabel = benchmarkPopulationLabel(drawnBenchmarkSeriesIds);
  /** The levels actually on the plot, in chain order — one caveat each. */
  const drawnBenchmarkLevels = BENCHMARK_LEVELS.filter((level) =>
    drawnBenchmarkSeriesIds.some((id) => benchmarkLevel(id) === level),
  );
  // The axis refusal speaks about series that were never drawn AT ALL, so it
  // has to read the input rather than the model.
  const heldBenchmarkLabel = benchmarkPopulationLabel(
    benchmarkSeries.filter((s) => s.points.length > 0).map((s) => s.seriesId),
  );

  /** Whether THIS level's own line made it onto the legend. */
  const levelIsListed = (level: BenchmarkLevel) =>
    chart.lines.some(
      (l) =>
        l.id.startsWith(BENCHMARK_LINE_PREFIX) &&
        benchmarkLevel(l.id.slice(BENCHMARK_LINE_PREFIX.length)) === level,
    );

  // The two lines' read health, each carrying its own name so a failure
  // sentence can never blame the wrong one.
  //
  // `listed` is per LEVEL, not global, and that distinction is the whole of
  // which sentence a reader gets. A globally-gated version printed
  // "Großmarktpreise: unvollständig geladen — die Referenzlinie kann Lücken
  // haben" when the Großmarkt read had failed OUTRIGHT and there was no such
  // line on the chart at all — the consumer line's mere presence was enough to
  // pick the softer wording. "Part of it is missing" about a line that is
  // entirely absent is the wrong claim in the more dangerous direction.
  const benchmarkReadHealth = [
    {
      key: "consumer" as const,
      label: BENCHMARK_LEVEL_LABEL.consumer,
      degraded: consumerBenchmarkDegraded,
      truncated: consumerBenchmarkTruncated,
      listed: levelIsListed("consumer"),
    },
    {
      key: "trade" as const,
      label: BENCHMARK_LEVEL_LABEL.trade,
      degraded: tradeBenchmarkDegraded,
      truncated: tradeBenchmarkTruncated,
      listed: levelIsListed("trade"),
    },
  ];

  // Levels the server looked for and this item simply has none of. Reported
  // only when the OTHER line is on the plot: two "kein Index" notes under an
  // empty chart is noise, but one missing line beside a drawn one is exactly
  // the case a reader misreads as a defect. A level whose read FAILED is
  // excluded — its own sentence is more specific and already says more.
  const absentBenchmarkLevels = benchmarkOnPlot
    ? BENCHMARK_LEVELS.filter(
        (level) =>
          benchmarkLevels.includes(level) &&
          !benchmarkSeries.some(
            (s) => benchmarkLevel(s.seriesId) === level && s.points.length > 0,
          ) &&
          !benchmarkReadHealth.some((h) => h.key === level && (h.degraded || h.truncated)),
      )
    : ([] as BenchmarkLevel[]);

  // Only the LIVE lines are legend-toggleable; an overlay is switched by its
  // year chip, which owns both of its sides at once.
  const liveLines = useMemo(
    () => chart.lines.filter((l) => l.offset === 0),
    [chart.lines],
  );

  // #2486, generalised: the hide-set is component state that survives an item
  // switch, a view switch and a grain switch, but the ">= 1 visible" guard is
  // only evaluated against the lines live at toggle time. Reconcile it to the
  // CURRENT line set: drop ids that no longer exist, and if the survivors would
  // hide EVERY line (a blank chart despite real data) reset to all-visible.
  // Referential identity is preserved when nothing changes, so a valid hide
  // causes no re-render.
  const liveLineIds = useMemo(
    () => liveLines.map((l) => l.id).join("␟"),
    [liveLines],
  );
  useEffect(() => {
    setHiddenLineIds((prev) => {
      if (prev.size === 0) return prev;
      // The OWN lines only (HOF-269 §1). This set governs `hiddenLineIds`,
      // which no longer holds a benchmark id — the level default does — and
      // reading the benchmark lines here would break the reset in both
      // directions: `wouldBlank` could never fire while a benchmark line
      // existed, and if it did fire it would clear a hide the reader never
      // made. An item with no own live line therefore has an EMPTY `present`,
      // and the guard below leaves the mount default standing.
      const present = new Set(
        liveLineIds
          .split("␟")
          .filter(Boolean)
          .filter((id) => !id.startsWith(BENCHMARK_LINE_PREFIX)),
      );
      const pruned = new Set([...prev].filter((id) => present.has(id)));
      const wouldBlank =
        present.size > 0 && [...present].every((id) => pruned.has(id));
      const next = wouldBlank ? new Set<string>() : pruned;
      if (next.size === prev.size && [...prev].every((id) => next.has(id)))
        return prev;
      return next;
    });
  }, [liveLineIds]);

  /**
   * Would anything still be drawn once `going` went dark?
   *
   * The "never hide the last one" guard, asked as a question about the RESULT
   * rather than as a count of what is on the plot now. The two came apart the
   * moment a toggle stopped being one line: a benchmark chip switches a
   * visibility KEY, and one key can own several lines — the
   * `__bio`/`__konventionell` split is still parsed for the drill-down ids and
   * for deploy skew, and both of those series are `consumer`. Two such lines
   * alone on the plot made a bare count read 2, so the guard permitted a
   * toggle that hid both and left the reader an empty chart (codex round 1,
   * P1). Reading each spec's own `hidden` and not `hiddenLineIds`, because
   * visibility has two sources and the guard must see what the reader sees.
   */
  const survivesHiding = (going: (l: AggLineSpec) => boolean) =>
    liveLines.some((l) => !l.hidden && !going(l));

  const toggleLine = (id: string) => {
    if (id.startsWith(BENCHMARK_LINE_PREFIX)) {
      const key = benchmarkVisibilityKey(id);
      setShownBenchmarkKeys((prev) => {
        if (!prev.has(key)) return new Set([...prev, key]);
        // The same last-line guard, and it has to be the same one: a reader who
        // hid `Verkauf` while a benchmark carried the plot may not then empty
        // the plot from the other end.
        if (
          !survivesHiding(
            (l) =>
              l.id.startsWith(BENCHMARK_LINE_PREFIX) &&
              benchmarkVisibilityKey(l.id) === key,
          )
        )
          return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }
    setHiddenLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      // Never hide the last one: a legend that can empty the plot leaves the
      // reader looking at a bug they have to guess their way out of. And it
      // never force-DRAWS anything to satisfy itself — a refused hide leaves
      // every line exactly where the reader put it.
      else if (survivesHiding((l) => l.id === id)) next.add(id);
      return next;
    });
  };

  // #2652 — whether a carry-forward is actually ON THE PLOT. Read by both the
  // `<Line>` layer and the footer copy, so the glyph and the sentence describing
  // it cannot end up disagreeing.
  const bridgesOnPlot = chart.drawn.some((l) => l.bridges.length > 0);
  // #2777 — the same rule for the reference LEVEL.
  const levelOnPlot = estimateMarks.length > 0 && chart.hasPlot;

  /**
   * HOF-96 §3 — the axis labels every month, but only where twelve labels fit.
   *
   * `width` is 0 until the plot has been laid out (and always 0 under jsdom),
   * and the gate is written as "dense AT OR ABOVE a threshold" so that unknown
   * width falls on the side that cannot collide. `plotRef` is passed straight
   * through to the element — wrapping it in an inline arrow would re-attach the
   * observer on every render, including the one its own measurement causes.
   */
  const { ref: plotRef, width: plotBoxPx } = useElementWidth<HTMLDivElement>();
  const monthlyLadder =
    innerPlotWidthPx(plotBoxPx) >= MONTHLY_TICKS_MIN_PLOT_PX;
  const axisTicks = useMemo(
    () =>
      timeAxisTicks(
        chart.domain.lo,
        chart.domain.hi,
        monthlyLadder ? MONTHLY_TICK_OPTS : undefined,
      ),
    [chart.domain.lo, chart.domain.hi, monthlyLadder],
  );

  const loneObs = useMemo(() => loneObservations(chart), [chart]);

  /**
   * HOF-120 — the per-bucket dot for one line, or `false` at 1T. See
   * {@link OBS_DOT_R} for why the grain decides it.
   *
   * No filtering of WHERE it lands, deliberately: recharts draws one dot per
   * non-null value of the segment's own `dataKey`, and the model writes that key
   * only on the buckets this line actually observed — nulls everywhere else,
   * including the rows another line owns on the shared union grid (#2488). So
   * "a dot at every observed bucket and nowhere else" is a property of the
   * model, not a rule re-derived here that could drift from it.
   *
   * Overlays get dots too. They are observations — a prior-year line is
   * interpolated across its gaps exactly as the live one is, and the reader
   * turned it on to compare seasons, which a stroke that hides where last
   * year's data stops cannot support. It is the same call `loneObservations`
   * already makes. The selection RING stays live-only for its own reason (it
   * promises a panel breakdown); a plain dot promises nothing but "an
   * observation is here", which is true on an overlay. `fillOpacity` mirrors the
   * stroke's, so an overlay's dots stay as subordinate as its line.
   */
  const obsDot = (line: AggLineSpec) =>
    grain === "day"
      ? (false as const)
      : {
          r: OBS_DOT_R,
          fill: line.color,
          stroke: "none",
          strokeWidth: 0,
          fillOpacity: line.offset === 0 ? 1 : 0.85,
        };

  // #2590 — an explicit y-domain over what is plotted, rounded outward onto
  // round numbers. recharts nices only an `'auto'` domain and would otherwise
  // divide the raw extent into equal parts (`4,2 / 4,9 / 5,6 …`), so the ticks
  // have to be pinned as well as the bounds.
  const yScale = useMemo(
    () =>
      chart.yExtent
        ? niceLinearScale(chart.yExtent.lo, chart.yExtent.hi)
        : null,
    [chart.yExtent],
  );

  // ── The selected bucket ───────────────────────────────────────────────────
  //
  // Precedence (#2508): the reader's own click → the deep-linked `?d=` → the
  // latest observed bucket. Each candidate is RESOLVED TO ITS BUCKET first, so a
  // `?d=` minted at 1T grain still points at the right point after a switch to
  // 1M — and so a click held across a grain switch does too, instead of silently
  // falling through and blanking the panel.
  const [picked, setPicked] = useState<string | null>(null);
  // `picked` survives a prop change (this component stays mounted across an item
  // switch), so it must not outrank a NEWLY addressed day: reset it whenever the
  // deep-linked day or the item changes, else a stale click would swallow the
  // `?d=` the URL now carries.
  useEffect(() => {
    setPicked(null);
  }, [day, item.id]);
  const observedBuckets = useMemo(
    () => new Set(model.bucketDates),
    [model.bucketDates],
  );
  /**
   * The buckets a drawn benchmark line actually carries a value in (HOF-248).
   *
   * Selectable in their own right, because otherwise the benchmark drill-down
   * is unreachable: `observedBuckets` is the OWN model's, so a click on a
   * benchmark point — and a `?d=` addressing one — was refused, and on a
   * benchmark-ONLY item there was no own bucket to fall back to either. That
   * left the panel's collector names and source links as code no tenant
   * without own deals in that item could ever reach.
   */
  const benchmarkBuckets = useMemo(() => {
    const dates = new Set<string>();
    for (const l of chart.drawn) {
      if (l.offset !== 0 || !l.id.startsWith(BENCHMARK_LINE_PREFIX)) continue;
      for (const row of chart.rows) {
        const seg = l.segments.find(
          (s) => row.ts >= s.fromTs && row.ts <= s.toTs,
        );
        if (seg && typeof row[seg.key] === "number") dates.add(row.date);
      }
    }
    return dates;
  }, [chart.drawn, chart.rows]);

  const inBucket = (d: string | null): string | null => {
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    const start = epochDayToIso(bucketStartEpochDay(toEpochDay(d), grain));
    return observedBuckets.has(start) || benchmarkBuckets.has(start)
      ? start
      : null;
  };
  // The fallback is the latest bucket a VISIBLE line actually reaches, not the
  // latest in the model.
  //
  // `panelColumns` drops the sides the legend has hidden, so those two notions
  // came apart the moment HOF-6 §5 made the legend a visibility control: with
  // Kauf hidden but owning the newest bucket, the chart defaulted to a date the
  // panel had no column for, and the reader's Verkauf drill-down vanished on a
  // click that was only supposed to hide a line. Read off the drawn segments —
  // `toTs` is each one's last observed bucket — so the answer cannot disagree
  // with what is on the plot. Live lines only: an overlay has no panel column at
  // all (see the selection ring).
  const lastVisibleBucket = useMemo(() => {
    let latest = -Infinity;
    for (const l of chart.drawn) {
      if (l.offset !== 0) continue;
      // The benchmark is context, not subject: the DEFAULT selection must not
      // move because a reference line was drawn — the reader lands on the
      // farm's own latest bucket, exactly as before HOF-19.
      if (l.id.startsWith(BENCHMARK_LINE_PREFIX)) continue;
      for (const seg of l.segments) if (seg.toTs > latest) latest = seg.toTs;
    }
    return latest === -Infinity ? null : isoOfTs(latest);
  }, [chart.drawn]);
  /**
   * The reference's own latest bucket — the fallback of LAST resort (HOF-248).
   *
   * Reached only when no own line reaches any bucket, so HOF-19's rule above is
   * untouched: where the farm has its own data the reader still lands on the
   * farm's own latest bucket, and the benchmark never moves the selection. On a
   * benchmark-only item there is nothing else to land on, and landing nowhere
   * means the drill-down cannot be opened at all.
   */
  const lastBenchmarkBucket = useMemo(() => {
    let latest: string | null = null;
    for (const date of benchmarkBuckets) if (!latest || date > latest) latest = date;
    return latest;
  }, [benchmarkBuckets]);
  const activeDate =
    inBucket(picked) ?? inBucket(day) ?? lastVisibleBucket ?? lastBenchmarkBucket;
  const activeTs = activeDate ? tsOfIso(activeDate) : null;

  /**
   * HOF-96 §4 — the price of the selected bucket on each live line, and the row
   * each label takes so two of them cannot land on each other.
   *
   * `assignLabelSlots` is #2540's primitive, reused rather than re-implemented:
   * it already answers exactly this question for the "Heute" labels, it is pure,
   * exported and unit-tested, and two stacking rules at two pitches on one plot
   * would be two answers to one question. The span is the plotted y-extent, the
   * same input the today-labels are slotted against — the threshold is a
   * FRACTION of it, so both stacks mean the same thing on a 0,50 €/kg item and
   * on a 30 €/kg one.
   *
   * Live lines only, for the reason the selection RING is already live-only: the
   * panel's columns come from the current aggregate, so a 2023 value beside a
   * 2026 dot would promise a breakdown the panel does not carry.
   */
  const selectedMarks = useMemo(() => {
    if (activeDate == null) return [];
    return chart.drawn.flatMap((l) => {
      if (l.offset !== 0) return [];
      // Own lines only, like the ring: the selected-price labels annotate the
      // farm's own prices, and the benchmark's number for this bucket lives in
      // its panel row below.
      if (l.id.startsWith(BENCHMARK_LINE_PREFIX)) return [];
      const value = selectedValue(chart.rows, activeDate, l);
      return value == null ? [] : [{ line: l, value }];
    });
  }, [chart.drawn, chart.rows, activeDate]);
  const selectedSlots = useMemo(
    () =>
      assignLabelSlots(
        selectedMarks.map((m) => ({ key: m.line.id, value: m.value })),
        chart.yExtent ? chart.yExtent.hi - chart.yExtent.lo : 0,
      ),
    [selectedMarks, chart.yExtent],
  );

  return (
    <div className="preisspiegel-chart">
      {/* HOF-6 §6 — ONE row: the segmented controls on the left, the legend
          (which is also the visibility control) on the right. It wraps rather
          than scrolls, so a narrow viewport degrades to two rows instead of
          hiding a control off-screen. */}
      <div className="preisspiegel-chart__toolbar">
        <div className="preisspiegel-chart__controls">
          <Segment
            label="Zeitraster"
            values={GRAINS}
            labelOf={(g) => GRAIN_LABELS[g]}
            titleOf={(g) => GRAIN_TITLES[g]}
            selected={grain}
            available={GRAINS}
            onSelect={onGrainChange}
          />
          {item.quality && qualities.length > 0 && (
            <Segment
              label="Qualität"
              values={QUALITIES}
              labelOf={(q) => QUALITY_LABELS[q]}
              selected={quality}
              available={qualities}
              onSelect={onQualityChange}
            />
          )}
          {hasChannels && (
            <Segment
              label="Ansicht"
              values={VIEWS}
              labelOf={(v) => VIEW_LABELS[v]}
              titleOf={(v) => VIEW_TITLES[v]}
              selected={effView}
              available={VIEWS}
              onSelect={onViewChange}
            />
          )}
        </div>

        <div
          className="preisspiegel-chart__legend"
          role="group"
          aria-label="Linien"
        >
          {liveLines.map((l) => (
            <LegendChip
              key={l.id}
              label={l.label}
              color={l.color}
              dash={l.dash}
              pressed={!l.hidden}
              value={
                l.todayValue != null
                  ? `${priceFmt(l.todayValue)} ${unitLabel}`.trim()
                  : undefined
              }
              onToggle={() => toggleLine(l.id)}
              // HOF-214 — only the two headline chips explain themselves: the
              // aggregate Verkauf line (a mengengewichteter Durchschnitt) and
              // the benchmark lines (chained matched-model indexes). Channel and Kauf
              // chips name populations the reader already picked; these two
              // name STATISTICS nothing else on screen defines.
              info={
                l.id.startsWith(BENCHMARK_LINE_PREFIX)
                  ? BENCHMARK_CHIP_INFO[
                      benchmarkLevel(l.id.slice(BENCHMARK_LINE_PREFIX.length)) ??
                        "consumer"
                    ]
                  : l.label === SIDE_LABELS.verkauf
                    ? verkaufChipInfo(today)
                    : undefined
              }
              testAttrs={{ "data-line-id": l.id }}
            />
          ))}
          {/* HOF-310 — the Volumen chip. A legend entry like the others, because
              the pane IS a series the reader switches: putting it in the toolbar's
              left-hand switcher group would make it read as a presentation mode
              (like Zeitraster or Ansicht) rather than as one more thing on the
              plot. Last among the line chips and before the year chips, matching
              the plot's own z-order — the columns sit under the lines. */}
          <LegendChip
            key="volume"
            label={VOLUME_LABEL}
            color={VOLUME_COLUMN_COLOR}
            pressed={volumeOn && volumeAvailable}
            disabled={!volumeAvailable}
            title={volumeChipTitle}
            onToggle={() => setVolumeOn((on) => !on)}
            testAttrs={{ "data-testid": "volume-chip" }}
          />
          {/* The prior-year chips ARE the overlays' legend entries — enabling one
              is what puts its line on the plot, so a separate swatch beside the
              chip would be the same control listed twice. */}
          {PRIOR_YEAR_OFFSETS.map((offset, i) => {
            const year = priorYearLabel(Number(today.slice(0, 4)), offset);
            const off = effView === "kanal";
            return (
              <LegendChip
                key={`py-${offset}`}
                label={year}
                color={PRIOR_YEAR_COLORS[i % PRIOR_YEAR_COLORS.length]}
                // HOF-96 AC 9 — `pressed` on this legend means "this line is on
                // the plot", which is what it means for every live chip
                // (`pressed={!l.hidden}`). In `kanal` no overlay is drawn, so a
                // chip left pressed would be the legend and the plot disagreeing
                // — the one thing #2506's rule forbids. The reader's `?py=`
                // choice is not lost: it is in the URL, and it comes back the
                // moment they switch to `Alle zusammen`.
                pressed={overlayYears.includes(offset)}
                // Disabled PER OPTION, never per group (`ChartPresentationToggle`'s
                // house rule): an unavailable option carries its own reason, so a
                // reader who lands on one is not left hunting for a caption
                // somewhere else on the toolbar.
                disabled={off}
                title={
                  off
                    ? `Vorjahr ${year}: in „Nach Kanal“ nicht verfügbar — diese Ansicht zeichnet bereits eine Linie je Kanal. Zurück zu „Alle zusammen“ blendet die Auswahl wieder ein.`
                    : `Vorjahr ${year} überlagern`
                }
                onToggle={() => onTogglePriorYear(offset)}
                testAttrs={{ "data-prior-year": String(offset) }}
              />
            );
          })}
        </div>
      </div>

      {/* #2777 — the reference price, in words, above the plot.

          Stated here as well as drawn because the level alone is not reliable
          communication: the book anchor sits in 2023 while the window is the
          last year, so on most items the dashed line is outside the axis and the
          reader would see nothing at all. It also has to survive the case with
          no plot — an estimate-only item (every #2694 Saatgut tile) renders
          "Keine Daten für diese Auswahl." below, and this sentence is the
          difference between that reading as "we know nothing about this item"
          and "nothing was traded, and here is what the book says". */}
      {estimateMarks.map((mark) => (
        <p
          className="preisspiegel-chart__estimate"
          key={mark.side}
          data-side={mark.side}
        >
          <EstimatedChip />
          <span>
            Referenzpreis{namesSide ? ` ${SIDE_LABELS[mark.side]}` : ""}{" "}
            <span className="preisspiegel-chart__estimate-value">
              {priceFmt(mark.value)} {estimateUnitLabel}
            </span>{" "}
            · Stand {formatDayDe(mark.date)}
          </span>
        </p>
      ))}

      {/* HOF-19 AC 7 — what each drawn line IS, in words, on the screen.

          Not a tooltip, deliberately: the farmer must not have to hover to
          learn that these lines are different KINDS of price from his own, and
          from each other.

          ONE SENTENCE PER LEVEL, because they say opposite things. A single
          merged caveat read "Verbraucherpreise & Großmarktpreise:
          Endverbraucherpreise mit unbekanntem Händler-Aufschlag" — written
          when there could only be one population, and now telling the farmer
          that the Großmarkt notation he is actually measured against carries a
          retail markup. That is the exact confusion the two-level split exists
          to remove, restated by the chart itself.

          Read off the DRAWN lines and gated on the model rather than
          recomputed, so a sentence and its glyph cannot disagree. The
          glossary's terms, no new compounds. */}
      {drawnBenchmarkLevels.map((level) => (
        <p key={level} className="preisspiegel-chart__benchmark-note">
          {BENCHMARK_LEVEL_CAVEAT[level]}
        </p>
      ))}

      {/* AC 9 — the benchmark exists but cannot share this axis. Said rather
          than silently dropped: a benchmark that quietly vanishes on €/dt items
          reads as "AMI has nothing here", which is a statement about the market
          this page has no licence to make. Never converted — a derived number
          has no place on a chart whose argument is that it shows
          observations. */}
      {benchmarkHasData && !benchmarkCoPlottable && heldBenchmarkLabel && (
        <p className="preisspiegel-chart__benchmark-note">
          {heldBenchmarkLabel} ({BENCHMARK_UNIT}) wird nicht angezeigt: dieses
          Produkt wird in {published.mixed ? "mehreren Einheiten" : unitLabel}{" "}
          geführt.
        </p>
      )}

      {/* A level the server looked for and this product simply has none of.

          Coverage is asymmetric by source and always will be — the retail
          collectors reach the four berries, the Großmarkt report quotes
          Erdbeeren and Kirschen — so one line missing beside another is normal.
          Unsaid, it reads as a broken chart, and the reader's repair is to
          assume the market has no data at this level, which is a claim this
          page has no basis for. */}
      {absentBenchmarkLevels.map((level) => (
        <p
          key={level}
          className="preisspiegel-chart__benchmark-note"
          data-absent-level={level}
        >
          {BENCHMARK_LEVEL_ABSENT[level]}
        </p>
      ))}

      {/* The READ failed entirely and left no line — a different sentence from
          "no data", because only one of the two is a statement about the
          market. Gated on THIS level's own line, not on any benchmark being
          listed: a failed trade read beside a drawn consumer line is still a
          line that is wholly absent. */}
      {benchmarkReadHealth
        .filter((p) => !p.listed && p.degraded)
        .map((p) => (
          <p
            key={p.key}
            className="preisspiegel-chart__benchmark-note preisspiegel-chart__benchmark-note--failed"
            data-population={p.key}
          >
            {p.label} konnte nicht geladen werden.
          </p>
        ))}

      {/* The read PARTIALLY failed or hit its cap while data IS on screen
          (codex P1). The earlier build suppressed any failure copy the moment
          a line was listed, so a truncated AMI read drew as a complete market
          reference — the #2573 shape, one population over. The drawn line is
          real; the sentence says it may not be all of it. */}
      {benchmarkReadHealth
        .filter((p) => p.listed && (p.degraded || p.truncated))
        .map((p) => (
          <p
            key={p.key}
            className="preisspiegel-chart__benchmark-note preisspiegel-chart__benchmark-note--failed"
            data-population={p.key}
          >
            {p.label}: unvollständig geladen — die Referenzlinie kann Lücken
            haben.
          </p>
        ))}

      {/* #2944 — this item's facts were published under more than one unit, so
          the Y-axis carries no label.

          Said in words because a blank axis is not communication: an unlabelled
          scale reads as a rendering glitch, and the reader's repair is to assume
          the unit they expected — which is the wrong answer the suppression
          exists to withhold. */}
      {published.mixed && (
        <p className="preisspiegel-chart__unit-mixed" role="alert">
          <strong>Einheiten uneinheitlich.</strong> Für diese Position liegen
          Preise in mehreren Einheiten vor. Die Achse trägt deshalb keine
          Einheit — und die Kurve vergleicht Werte, die nicht in derselben
          Einheit erfasst wurden.
        </p>
      )}

      {chart.hasPlot ? (
        <div className="preisspiegel-chart__plot" ref={plotRef}>
          <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX}>
            <ComposedChart
              data={chartRows}
              margin={CHART_MARGIN}
              onClick={(state: {
                activeLabel?: string | number;
                activePayload?: { payload?: { date?: string } }[];
              }) => {
                // #2543 — on a numeric axis `activeLabel` is a TIMESTAMP, so the
                // clicked row's own ISO `date` is what the panel and `?d=` want.
                const row = state?.activePayload?.[0]?.payload;
                const iso =
                  row?.date ??
                  (typeof state?.activeLabel === "number"
                    ? epochDayToIso(Math.floor(state.activeLabel / 86_400_000))
                    : null);
                if (iso) setPicked(iso);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis
                dataKey="ts"
                type="number"
                // Both mandatory: without `allowDataOverflow` recharts widens the
                // axis to fit the data and ignores the domain entirely — which
                // since HOF-6 would silently restore the old data-driven axis.
                domain={[chart.domain.lo, chart.domain.hi]}
                allowDataOverflow
                // Tick density is the generator's job (calendar boundaries at a
                // grain chosen from the span), not recharts' — hence interval 0.
                ticks={axisTicks.ticks}
                interval={0}
                minTickGap={0}
                tickFormatter={(v: number) =>
                  formatTimeTick(v, axisTicks.grain)
                }
                // The font size is {@link AXIS_TICK_METRICS}'s, because
                // MONTHLY_TICKS_MIN_PLOT_PX is derived from it — two spellings
                // of one number is how a width gate goes stale silently.
                tick={{ fontSize: AXIS_TICK_METRICS.fontPx, fill: "#6b7280" }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280" }}
                width={Y_AXIS_WIDTH_PX}
                tickFormatter={(v: number) => numberFmt(v, yScale?.decimals)}
                domain={yScale ? [yScale.min, yScale.max] : ["auto", "auto"]}
                ticks={yScale?.ticks}
                // MANDATORY, exactly as on the XAxis: without it recharts widens
                // the axis back out to fit the data and ignores the domain.
                // Unconditional — with an `'auto'` domain it is inert.
                allowDataOverflow
                label={{
                  value: unitLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { fontSize: 12, fill: "#6b7280" },
                }}
              />
              {/* Light crosshair only — the heavy tooltip is replaced by the
                  click-to-open detail panel below. */}
              <Tooltip
                cursor={{ stroke: "#94a3b8", strokeDasharray: "3 3" }}
                content={renderVolumeHint}
              />
              {/* HOF-310 — the Volumen pane. FIRST among the drawable marks, so
                  recharts (which paints children in source order) puts the
                  columns UNDER every line, dot and label that annotates them.

                  Its own hidden y-axis: the columns are kilograms and the plot
                  is euros per kilogram, and a tonnage measured against the price
                  scale would be a number read off the wrong ladder. The axis top
                  is what makes AC 2's "bottom 20–25 %" true — see
                  `volumeAxisTop`. Rendered only while the pane is drawn, so with
                  the chip off there is no second axis in the tree at all. */}
              {volumeDrawn && volumePane != null && (
                <YAxis
                  yAxisId={VOLUME_AXIS_ID}
                  hide
                  domain={[
                    0,
                    volumeAxisTop(volumeAxisMax(volumePane, visibleVolumeKeys)),
                  ]}
                  allowDataOverflow
                />
              )}
              {volumeDrawn &&
                volumePane != null &&
                volumePane.series
                  // `measured` as well as visible: an unmeasurable series exists
                  // only to carry its quantity-less deals into the hint, and an
                  // all-null `<Bar>` on the plot would be an element that can
                  // never draw.
                  .filter((v) => v.measured && visibleVolumeKeys.includes(v.key))
                  .map((v) => (
                    <Bar
                      key={v.key}
                      yAxisId={VOLUME_AXIS_ID}
                      dataKey={v.key}
                      name={v.label}
                      // One stack id across every segment: the channels of a
                      // bucket are ONE column, and separate stacks would draw
                      // them side by side as four thin columns per bucket.
                      stackId={VOLUME_STACK_ID}
                      fill={v.color}
                      barSize={volumeBarSizePx(grain, innerPlotWidthPx(plotBoxPx))}
                      isAnimationActive={false}
                      legendType="none"
                    />
                  ))}
              {/* #2501 — the 01.01 splitter, FIRST so the selection line and
                  "Heute" both stay on top of it (recharts source z-order).
                  A fixed one-year window contains exactly one of these, which
                  makes it more useful than it was on a pannable axis, not less:
                  it is the only cue that the left third of the plot is last
                  year — and it is what a prior-year overlay is measured against. */}
              {yearBoundaryLinesAt(chart.domain.lo, chart.domain.hi)}
              {/* #2777 — the curated reference price as a LEVEL, not a series.

                  A horizontal line is the only shape that does not lie about this
                  number: the book holds one anchored value with no history, so a
                  price line through it would draw movement nobody observed, and a
                  dot at the anchor would sit outside the window. Dashed on its own
                  pattern (never the forecast's `5 4`) so "carried forward from a
                  real observation" and "never observed at all" stay distinct. */}
              {levelOnPlot &&
                estimateMarks.map((mark) => (
                  <ReferenceLine
                    key={`ref-${mark.side}`}
                    y={mark.value}
                    stroke={ESTIMATE_LEVEL_COLOR}
                    strokeDasharray={ESTIMATE_LEVEL_DASH}
                    strokeOpacity={0.8}
                    label={{
                      // Named only when there are two to tell apart: on the
                      // one-sided item — most of the catalog — "Referenzpreis
                      // Verkauf" would be a distinction without a difference on a
                      // chart that draws one line.
                      value: namesSide
                        ? `Referenzpreis ${SIDE_LABELS[mark.side]} (${ESTIMATED_LABEL})`
                        : `Referenzpreis (${ESTIMATED_LABEL})`,
                      position: "insideBottomLeft",
                      style: { fontSize: 11, fill: ESTIMATE_LEVEL_COLOR },
                    }}
                  />
                ))}
              {activeTs != null && (
                <ReferenceLine
                  x={activeTs}
                  stroke="#0f766e"
                  strokeWidth={1.5}
                  strokeOpacity={0.5}
                />
              )}
              <ReferenceLine
                x={chart.todayTs}
                stroke="#9ca3af"
                strokeDasharray="2 3"
                // #2540's no-overlap AC, now held by SEPARATION IN Y rather than
                // in x: the caption sits in the top margin (see
                // TODAY_CAPTION_POSITION) and the today-price labels sit out at
                // their own levels inside the plot, so no string length can bring
                // them together. Pinned by geometry in todayLabelOffset.test.
                label={{
                  value: "Heute",
                  position: TODAY_CAPTION_POSITION,
                  fontSize: 11,
                  fill: "#6b7280",
                }}
              />
              {/* #2652 — the off-season carry-forwards, FIRST in source order so
                  the observed line paints over them rather than under (recharts
                  draws children in order). One <Line> per bridge, each with a
                  contiguous non-null run, so `connectNulls` can cross a
                  neighbouring line's rows without reaching into a later gap.

                  `type="linear"`, not the `monotone` the data lines use: a
                  carry-forward is flat by definition and a spline through two
                  equal points has nothing to interpolate, so linear states the
                  intent rather than relying on that coincidence.

                  `bridges` is EMPTY unless the read was certified complete, so
                  the suppression is one condition in the model rather than a
                  second one here that could drift. */}
              {chart.drawn.flatMap((l) =>
                l.bridges.map((b) => (
                  <Line
                    key={b.key}
                    type="linear"
                    dataKey={b.key}
                    // Named as the absence it is, so nothing can label a carried
                    // price with the series' own name.
                    name={`${l.label} (keine Beobachtung)`}
                    stroke={l.color}
                    strokeDasharray={BRIDGE_DASHARRAY}
                    strokeLinecap="round"
                    strokeOpacity={BRIDGE_OPACITY}
                    strokeWidth={BRIDGE_WIDTH_PX}
                    dot={false}
                    connectNulls={true}
                    isAnimationActive={false}
                    legendType="none"
                  />
                )),
              )}
              {/* #2543 — one <Line> per SEGMENT: a season gap is a structural
                  break between two dataKeys, which recharts cannot draw a path
                  across, so no `connectNulls` value could fabricate a winter
                  plateau.

                  HOF-6 §2 — `dot={false}` at 1T, where one dot per observed
                  bucket over a fixed year is a dotted rule rather than a mark.
                  HOF-120 revisited that at the two COARSE grains and split it:
                  at 52 or 12 points the dots say where the data is, which the
                  stroke cannot, and a sub-`GAP_BREAK_DAYS` gap is otherwise
                  interpolated silently. See {@link OBS_DOT_R}. */}
              {chart.drawn.flatMap((l) =>
                l.segments.map((seg) => (
                  <Line
                    key={seg.key}
                    type="monotone"
                    dataKey={seg.key}
                    name={l.label}
                    stroke={l.color}
                    {...(l.dash ? { strokeDasharray: l.dash } : {})}
                    strokeWidth={l.strokeWidth}
                    strokeOpacity={l.offset === 0 ? 1 : 0.85}
                    dot={obsDot(l)}
                    // #2488: several lines share one <ComposedChart> over the
                    // UNION of every line's dates, so each is null on dates owned
                    // only by another — an internal gap. connectNulls bridges
                    // those so a line renders as ONE continuous path WITHIN a
                    // segment. Segment breaks are unaffected: they are separate
                    // elements, not nulls.
                    connectNulls={true}
                    isAnimationActive={false}
                  />
                )),
              )}
              {chart.drawn
                .filter((l) => l.fcKey !== "")
                .map((l) => (
                  <Line
                    key={l.fcKey}
                    type="monotone"
                    dataKey={l.fcKey}
                    name={`${l.label} (Prognose)`}
                    stroke={l.color}
                    strokeDasharray="5 4"
                    strokeOpacity={0.5}
                    dot={false}
                    strokeWidth={2}
                    connectNulls={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                ))}
              {/* The observations a line has no way to draw — see
                  LONE_OBS_DOT_R. Before the selected/"Heute" markers in source
                  order because it is part of the line, and those annotate it. */}
              {loneObs.map((o) => (
                <ReferenceDot
                  key={`lone-${o.key}`}
                  className="preisspiegel-chart__lone-dot"
                  x={o.ts}
                  y={o.value}
                  r={LONE_OBS_DOT_R}
                  fill={o.color}
                  stroke="none"
                  ifOverflow="discard"
                />
              ))}
              {/* HOF-6 §2 — the dot that marks a CHOICE: the selected bucket, on
                  each drawn line that has a price there. It is what the panel
                  below is about, so it has to be findable without reading the
                  panel.

                  Live lines only. The panel's columns come from the CURRENT
                  aggregate, so a ring on a prior-year overlay would promise a
                  breakdown of a value the panel does not carry and point the
                  reader at the nearest column instead — a 2023 price read as
                  this month's. */}
              {activeTs != null &&
                selectedMarks.map(({ line: l, value }) => {
                  // Bound once: the string is both what the label PRINTS and
                  // what its side is measured against, and two spellings of it
                  // would be a rule about a label the chart does not draw.
                  const selectedLabelText =
                    `${priceFmt(value)} ${unitLabel}`.trim();
                  return (
                    <ReferenceDot
                      key={`sel-${l.id}`}
                      // Named, because the plot now carries TWO kinds of
                      // ReferenceDot and they mean different things: this one
                      // marks the bucket the panel is about, the "Heute" one
                      // marks a projection. Indistinguishable in the DOM, the two
                      // would be one refactor away from being counted as each
                      // other.
                      className="preisspiegel-chart__selected-dot"
                      x={activeTs}
                      y={value}
                      r={SELECTED_DOT_R}
                      fill="#fff"
                      stroke={l.color}
                      strokeWidth={SELECTED_DOT_STROKE_PX}
                      isFront
                      ifOverflow="discard"
                      // HOF-96 §4 — the price, beside the mark it describes. Same
                      // formatter and same unit as the "Heute" label: one plot
                      // must not spell one price two ways. In the line's own
                      // colour, so a label is attributable to a line without
                      // reading the panel — which is the whole reason for moving
                      // it here.
                      label={{
                        value: selectedLabelText,
                        // The side is measured, not assumed — the plot width the
                        // monthly ladder already reads, and this label's own
                        // string. See selectedLabelSide.
                        position: selectedLabelSide(activeTs, chart.domain, {
                          widthPx: innerPlotWidthPx(plotBoxPx),
                          text: selectedLabelText,
                        }),
                        offset: SELECTED_LABEL_OFFSET_PX,
                        dy: (selectedSlots[l.id] ?? 0) * TODAY_LABEL_ROW_PX,
                        fill: l.color,
                        fontSize: SELECTED_LABEL_METRICS.fontPx,
                        fontWeight: 600,
                        className: "preisspiegel-chart__selected-label",
                      }}
                    />
                  );
                })}
              {/* #2540 — today's projected level per drawn line, as a marker AND
                  as the number: the single figure a reader most wants ("what is
                  this worth today?") used to be obtainable only by clicking.
                  LAST in source order on purpose — recharts paints children in
                  order, so the marker sits on top of the dashed forecast line it
                  annotates rather than under it. */}
              {chart.drawn
                .filter(
                  (l): l is AggLineSpec & { todayValue: number } =>
                    l.todayValue != null,
                )
                .map((l) => (
                  <ReferenceDot
                    key={`today-${l.id}`}
                    className="preisspiegel-chart__today-dot"
                    x={chart.todayTs}
                    y={l.todayValue}
                    r={TODAY_DOT_R}
                    fill={l.color}
                    stroke="#fff"
                    strokeWidth={1}
                    isFront
                    ifOverflow="discard"
                    label={{
                      value: `${priceFmt(l.todayValue)} ${unitLabel}`.trim(),
                      position: "right",
                      offset: TODAY_LABEL_OFFSET_PX,
                      dy: l.labelSlot * TODAY_LABEL_ROW_PX,
                      fill: l.color,
                      fontSize: 11,
                      fontWeight: 600,
                      className: "preisspiegel-chart__today-label",
                    }}
                  />
                ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="preisspiegel-chart__empty" role="status">
          Keine Daten für diese Auswahl.
        </p>
      )}

      {/* `observedBuckets` is checked as well as `hasData` since HOF-248 made a
          BENCHMARK bucket selectable: the own panel answers "who paid the farm
          what in this bucket", and for a bucket the own model never observed
          the honest answer is to render nothing rather than an empty column
          set. A no-op before that change — every selectable date was an own
          one — and the reason the benchmark selection is safe now. */}
      {chart.hasData && activeDate && observedBuckets.has(activeDate) && (
        <BucketDetail
          model={model}
          columns={chart.panelColumns}
          date={activeDate}
          unit={unitLabel}
          itemId={item.id}
        />
      )}

      {/* HOF-19 Phase B / A4 — what the AMI point is made of, on demand. A
          sibling of the own-price panel rather than a column in it: the panel's
          columns answer "who paid the farm what", and a shelf visit in
          somebody else's shop is not one of those answers. */}
      {activeDate && benchmarkOnPlot && (
        <BenchmarkBucketDetail
          chart={chart}
          date={activeDate}
          grain={grain}
          itemId={item.id}
        />
      )}

      {/* The footer names exactly what is ON THE PLOT.

          HOF-6 rewrote the first clause (the statistic changed) and deleted the
          whisker one (the glyph is gone). `allWeighted` is read rather than
          assumed: a read path that carries no `provenance.amount` degrades every
          bucket to a plain mean, and claiming a weighting that did not happen is
          the quiet kind of wrong. The gap clause still branches on what is
          drawn, not on what the model could have drawn.

          The middle branch is the same principle one level finer: two branches
          could not describe a MIXED plot, and it is mixed in two independent
          ways. Within a bucket, `bucketPrice` drops every weightless observation
          the moment one observation carries a quantity, so the bucket is honestly
          weighted but over a subset. Across buckets, `allWeighted` only has to
          lose ONE to be false — a weighted live day beside an unweighted one, or
          a weighted live line beside an unweighted overlay. The old two-branch
          form overstated BOTH ends: "aller Geschäfte" named deals the number
          never saw, and "keine Mengen hinterlegt" denied the weighted buckets
          sitting on screen. The middle branch is what either case is true of.

          The LAST branch splits for the same reason, one level down. A bucket
          built only from cells UDB weighted over part of their rows is priced by
          the unweighted fallback, so it may not be called a weighted average —
          the panel labels that very bucket a plain `Ø`. But quantities plainly
          were recorded, so the parenthetical is the false half, not the
          sentence. Drop the parenthetical rather than promote the claim. */}
      <p className="preisspiegel-chart__basis">
        Ein Punkt = {GRAIN_POINT_COPY[grain]},{" "}
        {/* HOF-248 — with NO own line drawn, every clause of the own-price
            sentence was false in a way the reader could not detect. The
            weighting branches are computed over the own cells, so an empty own
            model made `allWeighted` vacuously true and the footer claimed a
            "mengengewichteter Durchschnitt aller Geschäfte" over zero deals;
            and "gestrichelt ab Heute = fortgeschrieben" named a forecast the
            reference deliberately never gets — under a line that is dashed end
            to end, which is exactly the glyph that sentence sends them to. */}
        {ownOnPlot ? (
          <>
            {chart.allWeighted && !chart.partiallyWeighted
              ? "Preis = mengengewichteter Durchschnitt aller Geschäfte"
              : chart.anyWeighted
                ? "Preis = mengengewichteter Durchschnitt der Geschäfte mit hinterlegter Menge"
                : chart.partiallyWeighted
                  ? "Preis = Durchschnitt der Beobachtungen"
                  : "Preis = Durchschnitt der Beobachtungen (keine Mengen hinterlegt)"}{" "}
            · gestrichelt ab „Heute“ = fortgeschrieben.{" "}
          </>
        ) : (
          <>
            Preis = Referenzwert
            {drawnBenchmarkLabel ? ` (${drawnBenchmarkLabel})` : ""}, keine
            eigenen Verkäufe in dieser Position.{" "}
          </>
        )}
        Die Zeitachse zeigt ein festes Jahr, „Heute“ steht fest im rechten
        Fünftel.{" "}
        {bridgesOnPlot && (
          <>
            Lücken von mehr als {chart.gapBreakDays / 7} Wochen ohne Beobachtung
            werden gepunktet überbrückt — das ist der zuletzt beobachtete Preis,
            fortgeschrieben, keine Messung.{" "}
          </>
        )}
        {/* The other half of the clause belongs to the POLICY, not to the
            absence of a bridge. With gaps bridgeable and simply none present —
            one unbroken season, the commonest chart there is — `!bridgesOnPlot`
            used to print "Lücken … werden nicht überbrückt", which is the
            opposite of what this chart would do with a gap. Nothing to say is
            the right amount to say. */}
        {!chart.gapsBridged && (
          <>
            Lücken von mehr als {chart.gapBreakDays / 7} Wochen ohne Beobachtung
            werden nicht überbrückt.{" "}
          </>
        )}
        {/* #2777 — named only when the level is ACTUALLY on the plot: copy that
            describes a glyph the reader cannot see points at nothing. */}
        {levelOnPlot && <>{ESTIMATED_LEVEL_CAPTION} </>}
        {/* HOF-19 — and the sentence for when the check itself did not run.
            UNCONDITIONAL on the marks being absent, deliberately: this is the
            one clause on this footer that must appear when there is nothing to
            see, because "nothing to see" is precisely what it is warning the
            reader not to conclude. Every other clause here describes a glyph;
            this one describes the absence of one. */}
        {marksUnavailable && <>{OUT_OF_BAND_UNAVAILABLE_CAPTION} </>}
        Klicke auf einen Punkt für alle Einzelpreise.
      </p>
    </div>
  );
}

/**
 * Every observed bucket that is alone in its segment — see {@link LONE_OBS_DOT_R}
 * for why those, and only those, are drawn as points.
 *
 * A singleton is `fromTs === toTs`: the model already split the runs, so this
 * reads the answer off them rather than re-deriving "isolated" from the rows and
 * risking a second, differently-drawn notion of where a season breaks.
 */
function loneObservations(
  chart: AggChartModel,
): { key: string; ts: number; value: number; color: string }[] {
  const out: { key: string; ts: number; value: number; color: string }[] = [];
  for (const line of chart.drawn) {
    for (const seg of line.segments) {
      if (seg.fromTs !== seg.toTs) continue;
      const row = chart.rows.find((r) => r.ts === seg.fromTs);
      const v = row?.[seg.key];
      if (typeof v === "number" && Number.isFinite(v)) {
        out.push({ key: seg.key, ts: seg.fromTs, value: v, color: line.color });
      }
    }
  }
  return out;
}

/** The value a line carries at the selected bucket, or null where it has none. */
function selectedValue(
  rows: readonly Record<string, number | string | null>[],
  date: string,
  line: AggLineSpec,
): number | null {
  const row = rows.find((r) => r.date === date);
  if (!row) return null;
  for (const seg of line.segments) {
    const v = row[seg.key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

const findBucket = (
  buckets: BucketAgg[] | undefined,
  date: string,
): BucketAgg | undefined => buckets?.find((b) => b.date === date);

/**
 * The drill-down under the plot: what one point on the chart is made of.
 *
 * Columns are the model's `panelColumns` — the FINEST breakdown the data
 * supports, not the one the plot happens to be drawing. A panel is a drill-DOWN,
 * so it should not get coarser because the chart above it was set to "Alle
 * zusammen". What HOF-6 did change is that every column is scoped to one SIDE:
 * a column mixing a purchase price with a sale price under one heading would be
 * a straightforward lie, and both sides are now on screen at once.
 *
 * Columns read their prices through the model's own `groupKey`, so the panel and
 * the plot cannot disagree about which observations belong where.
 */
function BucketDetail({
  model,
  columns,
  date,
  unit,
  itemId,
}: {
  model: AggregateModel;
  columns: readonly PanelColumn[];
  date: string;
  unit: string;
  /** HOF-19 — the catalog item, half of the drill-down's key. */
  itemId: string;
}) {
  const cols = columns
    .map((c) => ({
      col: c,
      bucket: findBucket(model.byGroup[c.groupKey], date),
    }))
    .filter((c) => c.bucket != null);
  if (cols.length === 0) return null;

  const totalLines = cols.reduce((sum, c) => sum + (c.bucket?.count ?? 0), 0);
  const period = cols[0].bucket as BucketAgg;
  // #2649 — rows sort by traded weight, but only where the source lines carry
  // one; with no weight anywhere the order falls back to price desc, and the
  // header must say which of the two the reader is looking at.
  const byWeight = cols.some((c) =>
    c.bucket?.uniqueSources.some((s) => s.kgSum != null),
  );
  return (
    <div className="preisspiegel-detail">
      <div className="preisspiegel-detail__head">
        {/* The PERIOD, not its first day: a weekly average captioned "17.08.2026"
            reads as one Monday's trading. */}
        <span className="preisspiegel-detail__date">
          {bucketLabel(period.date, period.endDate, model.grain)}
        </span>
        <span className="preisspiegel-detail__sub">
          {totalLines} {totalLines === 1 ? "Beobachtung" : "Beobachtungen"} ·{" "}
          {byWeight ? "nach Menge sortiert" : "nach Preis sortiert"}
        </span>
      </div>
      <div className="preisspiegel-detail__grid">
        {cols.map((c) => (
          <LineCol
            key={c.col.groupKey}
            title={c.col.label}
            color={c.col.color}
            bucket={c.bucket as BucketAgg}
            unit={unit}
            grain={model.grain}
            itemId={itemId}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Tooltip for a collapsed row (#2539): how many source lines it stands for, plus
 * their summed line total where every member carried one. Only rows that
 * actually collapsed carry it.
 */
function groupTitle(s: AggSourceGroup): string | undefined {
  if (s.lineCount <= 1) return undefined;
  const positions = `${s.lineCount} Positionen`;
  return s.amountSum == null
    ? positions
    : `${positions} · ${priceFmt(s.amountSum)} €`;
}

function LineCol({
  title,
  color,
  bucket,
  unit,
  grain,
  itemId,
}: {
  title: string;
  color: string;
  bucket: BucketAgg;
  unit: string;
  grain: Grain;
  itemId: string;
}) {
  // HOF-19 — which row's source lines are open, if any. ONE at a time and held
  // here rather than in each row: the panel is a reading surface, and several
  // expanded line lists at once turn it into a scroll. Keyed by the row's own
  // identity so re-rendering the list cannot transfer the open state to a
  // different row.
  const [openRow, setOpenRow] = useState<string | null>(null);
  // #2539 — rows are the DEDUPED observations: one fact per event_line means a
  // buyer with three positions on one Lieferschein would otherwise fill the list
  // with three identical rows (and push real sources past the fold).
  const top = bucket.uniqueSources.slice(0, 5);
  // Always surface flagged outliers, even past the top-5 cap: rows sort by
  // traded weight desc (#2649), so a mis-booking (€/Stück booked as €/kg) lands
  // wherever its tonnage puts it — and a small-quantity one sits at the tail,
  // exactly where the "+N weitere" fold would hide the very anomaly the flag is
  // meant to catch.
  // ...and HOF-19's band verdict for the stronger version of the same argument.
  // `isOutlier` is a relative hint; this one is an absolute verdict against a
  // declared window AND it is the row that was left out of the headline Ø above.
  // A row the reader is told about in the footnote but cannot find in the list
  // is the log-only failure this ticket exists to reject, one layer up. `||` and
  // not a second slice, so a row that is both is pinned once.
  const pinnedOutliers = bucket.uniqueSources
    .slice(5)
    .filter((s) => s.isOutlier || s.outOfBand !== undefined);
  const shown = [...top, ...pinnedOutliers];
  const extra = bucket.uniqueSources.length - shown.length;
  return (
    <div
      className="preisspiegel-detail__col"
      style={{ ["--cc" as string]: color } as CSSProperties}
    >
      <div className="preisspiegel-detail__col-head">
        <span className="preisspiegel-detail__sw" />
        {title}
      </div>
      <div className="preisspiegel-detail__med">
        {priceFmt(bucket.price)}{" "}
        <span className="preisspiegel-detail__u">
          {unit} {bucket.weighted ? "Ø gewichtet" : "Ø"}
        </span>
        {/* HOF-19 — this headline is folded over marked observations, because
            the bucket had no unmarked one to fold instead. Said HERE rather than
            only on the rows: the Ø is the number a reader takes away, and
            "every observation behind it is outside the expected range" is a
            different, larger claim than "one row did not count". Reachable and
            not rare — the artefact that prompted this ticket sat in a channel
            series with exactly one member.

            TEXT, not a colour, for the #3593 reason: a tint says "different"
            without saying how, and survives neither a screenshot nor a
            colour-blind reader. */}
        {bucket.allExcluded && (
          <span
            className="preisspiegel-detail__unweighted"
            title={OUT_OF_BAND_ALL_EXCLUDED_TITLE}
          >
            keine Beobachtung im Erwartungsbereich
          </span>
        )}
      </div>
      <div className="preisspiegel-detail__span">
        {/* #2671 — the Spanne is the TRUE min–max, and the label says so. The
            claim it earns is "both ends are prices that were really transacted",
            NOT "both ends are visible as rows": at cell grain a row renders its
            own volume-weighted mean, and the list is capped at five rows plus
            pinned outliers, so an extreme can legitimately sit outside what is on
            screen. An interpolated endpoint is not a price at all, which is the
            difference — and which is why the retired p10/p90 pair could not be
            quoted here.

            "Quellen" counts distinct counterparties (#2539); `count` is the
            LINE count, shown in the panel header. */}
        {/* A one-price bucket has no span, and "Spanne (Min–Max) 8,28–8,28"
            invites the reader to look for a difference that is not there. The
            single price is already the headline figure above, so the row drops
            to what it can still add: how many counterparties stand behind it. */}
        {bucket.obsMin === bucket.obsMax ? null : (
          <>
            Spanne (Min–Max) {priceFmt(bucket.obsMin)}–{priceFmt(bucket.obsMax)}{" "}
            ·{" "}
          </>
        )}
        {bucket.counterpartyCount}{" "}
        {bucket.counterpartyCount === 1 ? "Quelle" : "Quellen"}
      </div>
      <ul className="preisspiegel-detail__src">
        {shown.map((s) => {
          const rowTitle = groupTitle(s);
          // HOF-340 — the traded quantity, scaled and labelled in one step.
          // `kgSum` is `amount / value`, and that quotient is a quantity only
          // where the price numerator is €: on a `ct/l` tile it is hectolitres,
          // which #2944 labelled `l` and left unscaled. `null` here is the
          // deliberate refusal for a unit whose scale cannot be stated — see
          // {@link formatTradedQuantity}.
          const tradedQty =
            s.kgSum == null ? null : formatTradedQuantity(s.kgSum, unit);
          // #3593 — this row is listed under a headline it had no say in.
          // `bucketPrice` drops every observation without a usable quantity as
          // soon as ONE observation in the bucket has one, so on a weighted
          // bucket a quantity-less row is visibly present and provably excluded.
          //
          // Read off `weightShare`, which `collapseSources` derives with
          // `bucketPrice`'s OWN per-member predicate, NOT off `kgSum`: the sum
          // disagrees in both directions once a row collapses several members
          // (a positive+missing group nulls the sum though its weighted member
          // counted; a positive+0 group keeps a positive sum though its 0 kg
          // member was dropped). Only the per-member answer can label the row
          // by what it actually contributed.
          const share = bucket.weighted ? s.weightShare : "all";
          const rowId = `${s.counterparty}|${s.channel ?? ""}|${s.date}|${s.value}|${s.isOutlier}|${s.outOfBand?.direction ?? ""}|${s.seriesId ?? ""}`;
          return (
            <li
              key={rowId}
              className={[
                "preisspiegel-detail__src-row",
                s.isOutlier ? "is-out" : "",
                // HOF-19 — the dotted left rule, matching the marker below and
                // the ring on the plot, so the three glyphs read as ONE
                // statement rather than three unrelated decorations.
                s.outOfBand !== undefined ? "preisspiegel-outlier-row" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              {...(rowTitle ? { title: rowTitle } : {})}
            >
              {/* WHO and AT WHAT PRICE on the headline row; WHEN and HOW MUCH
                  demoted to a second, muted one.

                  It used to be one flex row of five items. HOF-6 broke that: at
                  1M a column holds many more rows, each now needing a date the
                  heading no longer carries, and "Nach Kanal" puts three or four
                  such columns side by side — at which point the only flexible
                  item, the counterparty, was ellipsised down to "FR…" and the
                  row named nobody. Everything on it is worth less than knowing
                  WHO the price came from. */}
              <span className="preisspiegel-detail__src-nm">
                {s.counterparty}
              </span>
              <span className="preisspiegel-detail__src-val">
                {priceFmt(s.value)} {unit}
              </span>
              <span className="preisspiegel-detail__src-meta">
                {/* A bucket can span a month, so WHICH day a row was booked on is
                    a fact the heading no longer carries. The year goes, since the
                    heading right above states it. */}
                {grain !== "day" && (
                  <span>{formatDayDe(s.date).slice(0, 6)}</span>
                )}
                {s.isOutlier && (
                  <span className="preisspiegel-detail__flag">Ausreißer</span>
                )}
                {/* HOF-19 — the marker, and it sits BESIDE "Ausreißer" rather
                    than replacing it, because the two are different claims and a
                    row can carry both. "Ausreißer" is relative to this bucket's
                    own price and says nothing about whether the number is
                    plausible; this names an absolute comparison against a
                    declared window, and it is the one that moved the Ø. */}
                {s.outOfBand !== undefined && (
                  <OutOfBandMarker mark={s.outOfBand} />
                )}
                {/* #2539 — quiet multiplicity marker on a row that collapsed
                    several source lines. Sized by `__src-meta` like its
                    siblings, so the class is now only a name for it. */}
                {s.lineCount > 1 && (
                  <span className="preisspiegel-detail__mult">
                    × {s.lineCount} Positionen
                  </span>
                )}
                {/* #2649 — the weight behind the price, and this list's sort key.
                    Omitted (never "0 kg", never "—") where the source lines carry
                    no quantity: an unknown weight is not a zero one. */}
                {tradedQty != null && (
                  // #2944 — denominated by the PRICE's denominator, not by an
                  // assumption that every catalog item is sold by weight; and
                  // HOF-340 — SCALED by the price's numerator too, since the
                  // denominator alone leaves a cent-quoted tile off by 100.
                  <span className="preisspiegel-detail__src-kg">
                    {tradedQty}
                  </span>
                )}
                {/* #3593 — TEXT, not a muted colour: a colour says "this row is
                    different" without saying how, and it survives neither a
                    screenshot nor a colour-blind reader. The label states the
                    CONSEQUENCE (it is not in the average above) because that is
                    the fact the reader is missing; the cause is already on
                    screen as the absent weight, and spelled out in the tooltip
                    for whoever stops on it. */}
                {share !== "all" && (
                  <span
                    className="preisspiegel-detail__unweighted"
                    title={
                      share === "none"
                        ? "Ohne verwertbare Menge — diese Beobachtung fließt nicht in den gewichteten Durchschnitt oben ein."
                        : "Nur die Positionen mit hinterlegter Menge fließen in den gewichteten Durchschnitt oben ein."
                    }
                  >
                    {share === "none"
                      ? "nicht im gewichteten Ø"
                      : "nur teilweise im gewichteten Ø"}
                  </span>
                )}
                {/* HOF-19 AC 10/11 — the drill-down, offered per row and fetched
                    only when pressed.

                    A row is a price collapse INSIDE one `(day × series)` cell,
                    so opening it asks for that cell — which is exactly the
                    request `/preisspiegel/lines` accepts and the only one it
                    accepts. Nothing is fetched on paint: a panel with twelve
                    rows issues zero requests until a human presses one of them,
                    which is what keeps the #2646 grain intact.

                    Offered only where the row can name its series. The
                    line-grain read path carries no `seriesId`, and a button that
                    cannot be answered is worse than no button. */}
                {s.seriesId !== undefined && (
                  <button
                    type="button"
                    className="preisspiegel-detail__drill"
                    aria-expanded={openRow === rowId}
                    onClick={() => setOpenRow(openRow === rowId ? null : rowId)}
                  >
                    {openRow === rowId ? "Positionen ausblenden" : "Positionen"}
                  </button>
                )}
              </span>
              {openRow === rowId && s.seriesId !== undefined && (
                <SourceLines
                  itemId={itemId}
                  seriesId={s.seriesId}
                  date={s.date}
                  unit={unit}
                />
              )}
            </li>
          );
        })}
        {extra > 0 && (
          <li className="preisspiegel-detail__src-row preisspiegel-detail__src-more">
            <span className="preisspiegel-detail__src-nm">
              +{extra} weitere
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * The source lines behind one cell, with each line's document (HOF-19 AC 11).
 *
 * This is where the drill-down stops being a promise. ``sourceDocId`` has been
 * parsed by ``ownPriceAdapter.ts:98`` since #2397 and rendered by nothing,
 * because #2646 moved the read to cell grain and a cell knows how many lines it
 * folded and what they were worth, not which document each came from. The link
 * is what makes a mark ACTIONABLE: a badged point nobody can trace back to a
 * delivery note is the log-only failure the ticket rejects, moved one layer up
 * where it merely looks better.
 *
 * ── Three states, and none of them is silence ────────────────────────────────
 *
 * Loading says so. A FAILED read says so, and says it differently from an empty
 * one — the #2573 lesson at the finest grain the app has: "we could not load
 * this" rendered as "there is nothing here" is how a farm with 20.000
 * observations gets shown the same screen as a farm with none. And a truncated
 * list names its own total, so "43 von 620" is legible rather than a prefix
 * sitting under a heading that contradicts it.
 */
function SourceLines({
  itemId,
  seriesId,
  date,
  unit,
}: {
  itemId: string;
  seriesId: string;
  /**
   * The ROW's own booked day — never the bucket's start.
   *
   * `AggSource.date` is the day one observation was booked on, and a bucket may
   * span several. Asking the server for `bucket="month"` here (codex round 2,
   * P1) expanded that day into its whole month, so pressing "Positionen" on ONE
   * daily row at grain 1M listed every line the series traded that month —
   * documents and prices misattributed to the row the reader had pointed at,
   * with no date on the list to give it away.
   */
  date: string;
  unit: string;
}) {
  // Always the DAY grain, and that is not a simplification: the row IS a day, at
  // every chart grain. The route's `month` mode exists for a caller drilling a
  // whole month CELL — it has no caller here, and inventing one by passing the
  // chart's grain answered a question nobody asked.
  const { data, isLoading, isError } = useBucketLines(
    { item: itemId, seriesId, date, bucket: "day" },
    true,
  );

  if (isLoading) {
    return (
      <p className="preisspiegel-detail__lines-note" role="status">
        Positionen werden geladen …
      </p>
    );
  }
  if (isError || data?.data_quality?.degraded) {
    return (
      <p className="preisspiegel-detail__lines-note" role="status">
        Die Einzelpositionen konnten nicht geladen werden.
      </p>
    );
  }
  const lines = data?.lines ?? [];
  if (lines.length === 0) {
    return (
      <p className="preisspiegel-detail__lines-note" role="status">
        Keine Einzelpositionen hinterlegt.
      </p>
    );
  }
  return (
    <>
      <ul className="preisspiegel-detail__lines">
        {lines.map((line, i) => (
          <li
            key={line.line_id ?? `${line.source_doc_id ?? "doc"}-${i}`}
            className="preisspiegel-detail__line"
          >
            <span className="preisspiegel-detail__line-nm">
              {line.product_name ?? "Position"}
            </span>
            <span className="preisspiegel-detail__line-val">
              {line.value != null
                ? `${priceFmt(line.value)} ${line.unit ?? unit}`
                : "—"}
            </span>
            {/* The line's own verdict, from the line's own published unit. This
                is the grain where the mark earns its keep: a cell can breach
                because ONE line of ten is a per-Kolli price published per kilo,
                and only here can a reader see which. */}
            {line.out_of_band && (
              <OutOfBandMarker
                mark={{
                  crop: line.out_of_band.crop,
                  unit: line.out_of_band.unit,
                  value: line.out_of_band.value,
                  publishedUnit: line.out_of_band.published_unit,
                  bandMin: line.out_of_band.band_min,
                  bandMax: line.out_of_band.band_max,
                  direction: line.out_of_band.direction,
                }}
              />
            )}
            {/* A real link, not a click handler: middle-click, "open in new tab"
                and the browser's own history all work, and the reader can see
                where it goes before pressing it. `/documents?doc=` is the
                DocumentsPage deep link (`searchParams.get('doc')`). */}
            {line.source_doc_id && (
              <Link
                className="preisspiegel-detail__line-doc"
                to={`/documents?doc=${encodeURIComponent(line.source_doc_id)}`}
              >
                Beleg
              </Link>
            )}
          </li>
        ))}
      </ul>
      {/* Named only when it fired — a cap the reader is told about on every
          complete list teaches them to ignore it on the one where it matters.

          It states the CAP and not a ratio. `total_rows` counts every row the
          twin holds for this cell including the curated price book, while the
          list above is booked lines only, so rendering "43 von 620" put two
          different populations either side of a "von" and handed the reader a
          discrepancy they could see and could not explain (codex round 2, P2).
          The number that is true and useful is how many are shown. */}
      {data?.data_quality?.truncated && (
        <p className="preisspiegel-detail__lines-note">
          Nicht alle Positionen dieser Zelle sind hier — die Anzeige ist auf{" "}
          {lines.length} begrenzt.
        </p>
      )}
    </>
  );
}


/**
 * The benchmark observations behind one drawn bucket — one CARD per level
 * (HOF-19 Phase B, restyled by HOF-269 §2).
 *
 * Values read OFF THE MODEL — the same rows the plot drew — so the cards and
 * the glyphs cannot disagree about what a point is. The rows behind a card are
 * fetched only when the reader presses for them (`useBenchmarkLines`,
 * enabled-gated), which is A4's whole discipline at this layer.
 *
 * **A section of its own, never merged into the own-channel grid.** The own
 * panel answers *who paid the farm what in this bucket*, and a shelf visit in
 * somebody else's shop is not one of those answers — the same cut the call site
 * makes. What §2 changes is only the GRAMMAR: the two levels used to render as
 * a flat footer strip beneath cards, so one click produced two visual languages
 * for the same kind of fact, and the weaker one carried the number the farmer is
 * being asked to benchmark against. The cards are built from `LineCol`'s own
 * classes rather than from a copy of them, so the two cannot drift.
 */
function BenchmarkBucketDetail({
  chart,
  date,
  grain,
  itemId,
}: {
  chart: AggChartModel;
  date: string;
  grain: Grain;
  itemId: string;
}) {
  const row = chart.rows.find((r) => r.date === date);
  const entries = chart.drawn
    .filter((l) => l.id.startsWith(BENCHMARK_LINE_PREFIX))
    .flatMap((l) => {
      if (!row) return [];
      const seg = l.segments.find((s) => row.ts >= s.fromTs && row.ts <= s.toTs);
      const value = seg ? row[seg.key] : null;
      if (typeof value !== "number") return [];
      const level = benchmarkLevel(l.id.slice(BENCHMARK_LINE_PREFIX.length));
      const facts = l.benchmarkBucketByDate?.[date];
      // `Ø` only where the number IS an average of the rows the drill-down
      // lists. Heading a chained index `Ø` promised the reader an arithmetic
      // they could check and would have failed: seen on the running stack as
      // `Ø 2,45 €/kg` above sixteen notations averaging 6,34. The method is in
      // the chip's ⓘ either way.
      //
      // Per BUCKET since HOF-250, not per level: a chained level re-anchors on
      // its own weighted mean wherever a week forms no relatives, so the
      // consumer line has weeks that ARE the mean of their rows sitting beside
      // weeks that are not. The level's declared method is the fallback for a
      // bucket that states none — a server that predates the field, so the card
      // reads exactly as the old tile did.
      const statistic =
        facts?.statistic ??
        (level ? BENCHMARK_LEVEL_STATISTIC[level] : undefined);
      return [{ line: l, level, value, facts, statistic }];
    });
  if (entries.length === 0) return null;

  /**
   * The header count, summed over the cards that can honestly state one.
   *
   * Only a level whose `n` counts observations contributes — see
   * {@link benchmarkCountIsObservations}. The chained level's same field counts
   * RELATIVES, and the server says so where it emits it
   * (`benchmark_index._chain_one_year`: *"one field meaning two things is how a
   * tile ends up promising an arithmetic the reader cannot check"*). Where
   * nothing can state one the clause is dropped entirely — silence rather than
   * a count of the wrong thing.
   */
  const observations = entries.reduce(
    (sum, e) => sum + (benchmarkCountIsObservations(e.level) ? (e.facts?.n ?? 0) : 0),
    0,
  );
  // The PERIOD, not its first day: a weekly average captioned "17.08.2026"
  // reads as one Monday's trading. `date` is already a bucket start — every
  // path into `activeDate` produces one — and it is normalised anyway rather
  // than relied on, because `bucketEndEpochDay` counts forward from whatever it
  // is handed and a mid-week day would silently end the label in the wrong
  // week.
  const endDate = epochDayToIso(
    bucketEndEpochDay(bucketStartEpochDay(toEpochDay(date), grain), grain),
  );

  return (
    <div className="preisspiegel-benchmark-detail">
      <div className="preisspiegel-detail__head">
        <span className="preisspiegel-detail__date">
          {bucketLabel(date, endDate, grain)}
        </span>
        {observations > 0 && (
          <span className="preisspiegel-detail__sub">
            {observations}{" "}
            {observations === 1 ? "Beobachtung" : "Beobachtungen"}
          </span>
        )}
      </div>
      <div className="preisspiegel-detail__grid">
        {entries.map(({ line, level, value, facts, statistic }) => (
          <BenchmarkBucketCard
            key={line.id}
            line={line}
            level={level}
            value={value}
            facts={facts}
            statistic={statistic}
            date={date}
            grain={grain}
            itemId={itemId}
          />
        ))}
      </div>
    </div>
  );
}

function BenchmarkBucketCard({
  line,
  level,
  value,
  facts,
  statistic,
  date,
  grain,
  itemId,
}: {
  line: AggLineSpec;
  level: BenchmarkLevel | null;
  value: number;
  facts: BenchmarkBucketFacts | undefined;
  statistic: BenchmarkStatistic | undefined;
  date: string;
  grain: Grain;
  itemId: string;
}) {
  const [open, setOpen] = useState(false);
  const seriesId = line.id.slice(BENCHMARK_LINE_PREFIX.length);
  // #2671's claim, one population over: both ends are prices that were really
  // observed. Rendered only where the bucket carried a range through the
  // re-bucket, and dropped — never widened to the value — where it did not.
  // A one-price bucket has no span, and "Spanne (Min–Max) 8,28–8,28" invites
  // the reader to look for a difference that is not there.
  const min = facts?.min;
  const max = facts?.max;
  const hasSpan =
    typeof min === "number" && typeof max === "number" && min !== max;
  // `Beobachtungen`, and never `Quellen`: the own panel's `n` is distinct
  // COUNTERPARTIES (#2539) and a benchmark bucket has no counterparty count at
  // all. Withheld on a level whose `n` is not observations — a different gate
  // from the `Ø` above, for the reason `benchmarkCountIsObservations` gives.
  const observations = benchmarkCountIsObservations(level) ? facts?.n : undefined;
  return (
    <div
      className="preisspiegel-detail__col"
      {...(level ? { "data-benchmark-level": level } : {})}
      style={{ ["--cc" as string]: line.color } as CSSProperties}
    >
      <div className="preisspiegel-detail__col-head">
        <span className="preisspiegel-detail__sw" />
        {line.label}
      </div>
      <div className="preisspiegel-detail__med">
        {statistic === "mean" ? "Ø " : ""}
        {priceFmt(value)}{" "}
        <span className="preisspiegel-detail__u">{BENCHMARK_UNIT}</span>
      </div>
      {(hasSpan || observations != null) && (
        <div className="preisspiegel-detail__span">
          {hasSpan && (
            <>
              Spanne (Min–Max) {priceFmt(min)}–{priceFmt(max)}
              {observations != null ? " · " : ""}
            </>
          )}
          {observations != null
            ? `${observations} ${observations === 1 ? "Beobachtung" : "Beobachtungen"}`
            : ""}
        </div>
      )}
      {/* The toggle, not an eager list: a rendered card costs nothing until a
          human asks — the same enforcement the own-price drill-down pins with
          its "fires NO request until it is pressed" test. Inside the card,
          because a card that lists eagerly costs two extra reads on every
          bucket click. */}
      <button
        type="button"
        className="preisspiegel-benchmark-detail__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Beobachtungen ausblenden" : "Beobachtungen anzeigen"}
      </button>
      {open && (
        <BenchmarkSources
          itemId={itemId}
          seriesId={seriesId}
          date={date}
          grain={grain}
        />
      )}
    </div>
  );
}

/**
 * A collector id (`public.rewe_shop`) as a reader's word for it.
 *
 * Falls back to the id with its namespace stripped rather than to a guess: an
 * unmapped collector is a real one whose name nobody has written down yet, and
 * showing `kaufland_offers` is honest where inventing a label would not be.
 */
/** The Shared Buy Index's price levels, in the channel model's own words. */
const DEAL_PRICE_LEVEL_LABEL: Record<string, string> = {
  grosshandel: "Großhandel",
  einzelhandel: "Einzelhandel",
};

function collectorLabel(source: string): string {
  const known: Record<string, string> = {
    "public.rewe_shop": "REWE Regal",
    "public.kaufland_offers": "Kaufland Angebot",
    "public.edeka_offers": "EDEKA Angebot",
    "public.aldi_sued_offers": "ALDI SÜD Angebot",
    "public.marktguru": "Prospekt (marktguru)",
    // HOF-544 — the Shared Buy Index: one aggregated row per price level.
    "hofsync.deals": "HofSync-Abschlüsse",
  };
  return known[source] ?? source.replace(/^public\./, "");
}

function BenchmarkSources({
  itemId,
  seriesId,
  date,
  grain,
}: {
  itemId: string;
  seriesId: string;
  date: string;
  grain: Grain;
}) {
  // The BUCKET — the chart's grain, floored at 'week' (HOF-214): the server
  // computes benchmark cells at week grain whatever the display bucket, so an
  // index point is never finer than a week. At 1T the point sits on the week's
  // Monday and stands for the whole week of shelf visits; asking for that one
  // day would silently omit the Tue–Sun rows behind it.
  const { data, isLoading, isError } = useBenchmarkLines(
    { item: itemId, seriesId, date, bucket: grain === 'day' ? 'week' : grain },
    true,
  );

  if (isLoading) {
    return (
      <p className="preisspiegel-detail__lines-note" role="status">
        Beobachtungen werden geladen …
      </p>
    );
  }
  if (isError || data?.data_quality?.degraded) {
    return (
      <p className="preisspiegel-detail__lines-note" role="status">
        Die Beobachtungen konnten nicht geladen werden.
      </p>
    );
  }
  const lines = data?.lines ?? [];
  // HOF-544 — the Shared Buy Index half of a trade point could not be listed. Said
  // in words: a Großmarkt list with no HofSync row beneath a composite point would
  // otherwise read as the point's whole provenance, which it is not.
  const dealsNote = data?.data_quality?.deals_degraded ? (
    <p className="preisspiegel-detail__lines-note" role="status">
      Die HofSync-Abschlüsse zu diesem Punkt konnten nicht geladen werden.
    </p>
  ) : null;
  if (lines.length === 0) {
    return (
      <>
        <p className="preisspiegel-detail__lines-note" role="status">
          Keine Beobachtungen hinterlegt.
        </p>
        {dealsNote}
      </>
    );
  }
  /**
   * EVERY file this list drew from, not the first one (HOF-248).
   *
   * The AMI panel is one producer publishing one workbook, so naming the first
   * row's was naming all of them. A public bucket is not: this very bucket
   * mixes `rewe_shop`, `kaufland_offers` and `edeka_offers`, and the footer
   * announced "Quelle: rewe_shop/…csv" over rows that came from neither of the
   * other two — a false attribution, and the one line on the panel whose whole
   * job is attribution.
   */
  const workbooks = [
    ...new Set(lines.map((l) => l.workbook).filter((w): w is string => Boolean(w))),
  ];
  return (
    <>
      <ul className="preisspiegel-benchmark-detail__lines">
        {lines.map((line, i) => (
          <li
            key={line.spot_id ? `${line.spot_id}-${i}` : i}
            className="preisspiegel-benchmark-detail__line"
          >
            <span
              className="preisspiegel-benchmark-detail__line-nm"
              {...(line.retailer ? { title: line.retailer } : {})}
            >
              {/* The shop, trimmed to what tells it from the next one — the
                  collectors disagree wildly about how much address belongs in
                  this field, and they land in ONE list. The untrimmed string
                  stays on the row's `title`, so nothing is destroyed. */}
              {[compactRetailerLabel(line.retailer, line.city), line.city]
                .filter(Boolean)
                .join(" · ") || "Beobachtung"}
              {line.pack_g ? ` · ${line.pack_g} g` : ""}
              {/* HOF-544 — an aggregated Shared Buy Index row names the PRICE
                  LEVEL it stands for and nothing finer: never a deal, a buyer or
                  a farm. */}
              {line.price_level ? ` · ${DEAL_PRICE_LEVEL_LABEL[line.price_level] ?? line.price_level}` : ""}
              {/* Where the fruit came from. On a Großmarkt row it is HALF the
                  cell key and the biggest single mover of the price — Spanish
                  strawberries at 3,15 against German at 8 — and such a row
                  carries no retailer and no pack size to tell it from the next
                  one. Without this, the Kirschen trade list read five identical
                  "deutschland" rows at five different prices. Labelled, because
                  the market is a place name too and two bare places in a row
                  cannot say which is which. */}
              {line.origin ? ` · Herkunft ${line.origin}` : ""}
              {/* Which collector saw it. Only where the row says so: the AMI
                  panel is one producer and needs no per-row label, while the
                  public population spans shelf reads and Prospekt offers. */}
              {line.source ? (
                <span className="preisspiegel-benchmark-detail__src">
                  {collectorLabel(line.source)}
                </span>
              ) : null}
            </span>
            <span className="preisspiegel-benchmark-detail__line-val">
              {line.value != null ? `${priceFmt(line.value)} €/kg` : "—"}
              {/* "Aktion" as recorded, tri-state: the vendor's own promo column
                  is unreliable row to row (#3590), so only an explicit yes is
                  said, and nothing is inferred from silence. */}
              {line.promo === "true" && (
                <span className="preisspiegel-benchmark-detail__promo">
                  Aktion
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {/* The benchmark's AC 11: the files this number came from. Not links —
          the workbook (or collector CSV) is an operator artefact, not a tenant
          document — but named, so "where is this from" has an answer on the
          screen.

          Since 2026-09-02 it is the ONLY attribution here. The rows used to
          carry a "Seite ansehen" link to the retailer's own page; Antonio had
          them removed. They were never durable — a shelf or Prospekt page is a
          live surface showing this week's offer, so the link needed an age gate
          to avoid handing the reader a different number under a label claiming
          it was the source — and the file named below is the archive that does
          not change under them. */}
      {workbooks.length > 0 && (
        <p className="preisspiegel-detail__lines-note">
          {workbooks.length === 1 ? "Quelle: " : "Quellen: "}
          {workbooks.join(" · ")}
        </p>
      )}
      {dealsNote}
      {data?.data_quality?.truncated && (
        <p className="preisspiegel-detail__lines-note">
          Nicht alle Beobachtungen dieser Zelle sind hier — die Anzeige ist
          begrenzt.
        </p>
      )}
    </>
  );
}
